import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext.jsx';
import Registratioon from './registratioon.jsx';
import ApplyProgramModal from './ApplyProgramModal.jsx';
import { extractFieldLines, stripDuplicateFieldLines, splitIntoBullets } from '../utils/programFields.js';
import SEO from './SEO.jsx';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { MARKET } from '../utils/market.js';
import './Programs.css';

const AiIcon    = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"/><circle cx="19" cy="4" r="2" opacity="0.6"/><circle cx="5" cy="19" r="1.5" opacity="0.4"/></svg>);
const AdminIcon = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>);
const OrgIcon   = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>);

const SOURCE_META = {
  ai:           { Icon: AiIcon,    cls: 'source-ai' },
  admin:        { Icon: AdminIcon, cls: 'source-admin' },
  organization: { Icon: OrgIcon, cls: 'source-organization' }, // Fixed: was 'source-org', now matches CSS
};


const PROGRAMS_BATCH_SIZE = 20;
const PROGRAMS_FETCH_TIMEOUT_MS = 6000;

// Turns a program's title into a readable, stable URL slug — crawlable, unique
// per opportunity (e.g. "/programs/erasmus-plus-internship-6710ab..."), unlike
// the old "?program=<id>" query param which every program shared the same
// generic /programs URL for, so Google had nothing distinct to rank per
// opportunity (e.g. a search for "Erasmus internship Armenia").
function slugifyProgramTitle(title) {
  return (title || '')
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'opportunity';
}
function programSlugPath(program) {
  const id = program.id || program._id;
  return `/programs/${slugifyProgramTitle(program.title)}-${id}`;
}
// A slug's trailing Mongo ObjectId (24 hex chars) is the only part that matters
// for lookup — the words before it are purely for readability/SEO.
function extractProgramIdFromSlug(slugId) {
  if (!slugId) return null;
  const match = slugId.match(/[0-9a-f]{24}$/i);
  return match ? match[0] : slugId;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = PROGRAMS_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: options.signal || controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

function extractProgramsPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.programs)) return payload.programs;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.programs)) return payload.data.programs;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function normalizeFetchedPrograms(programs, { markApproved = false, source = '' } = {}) {
  return extractProgramsPayload(programs)
    .filter((program) => program && typeof program === 'object')
    .map((program, index) => {
      const id = program.id || program._id || program.programId || `${source || 'program'}-${index}`;
      return {
        ...program,
        id,
        programId: program.programId || id,
        approval_status: markApproved ? (program.approval_status || program.status || 'approved') : program.approval_status,
        status: markApproved ? (program.status || program.approval_status || 'approved') : program.status,
        source: program.source || source || program.source,
      };
    });
}

function getSource(prog) {
  if (!prog) return 'ai';
  const pid = String(prog.programId || prog.id || prog._id || '');
  if (prog.submittedByType === 'organization' || prog.submitted_by_type === 'organization') return 'organization';
  if (prog.organizationName || prog.organization_name) return 'organization';
  if (prog.submitted_by_user || prog.submittedByUser) return 'organization';
  if (pid.startsWith('org-')) return 'organization';
  if (prog.submittedBy && typeof prog.submittedBy === 'object' && prog.submittedBy.accountType === 'organization') return 'organization';
  if (prog.approved_via === 'organization') return 'organization';
  if (prog.isManual === true) return 'admin';
  if (prog.approved_via === 'manual') return 'admin';
  if (pid.startsWith('manual-')) return 'admin';
  const src = (prog.source || '').toLowerCase();
  if (src === 'manual' || src === 'admin' || src === 'custom') return 'admin';
  if (prog.approved_via === 'ai' || prog.isAiGenerated) return 'ai';
  if (prog.scraping_id) return 'ai';
  if (src === 'ai' || src === 'scraping') return 'ai';
  if (pid.startsWith('sc-')) return 'ai';
  return 'ai';
}

function getSourceLabel(prog, t) {
  const src = getSource(prog);
  if (src === 'organization') {
    return prog.organization_name || prog.organizationName
      || (progsourceDisplay(prog) ? String(prog.source).trim() : '')
      || t('pages.programs.sourceByOrganization');
  }
  if (src === 'admin') return t('pages.programs.sourceByAdmin');
  return t('pages.programs.sourceByAi');
}

function progsourceDisplay(prog) {
  const s = String(prog?.source || '').trim().toLowerCase();
  if (!s) return false;
  if (['ai', 'scraping', 'manual', 'admin', 'custom', 'organization'].includes(s)) return false;
  return true;
}

function getYoutubeEmbedUrl(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const u = raw.trim();
  const m = u.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function getVimeoEmbedUrl(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const m = raw.trim().match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}` : null;
}

const getDefaultSectors = (t) => [
  { id: 'ecology',       label: t('pages.programs.sector_ecology'),       color: '#2E6B52', icon: 'fas fa-leaf' },
  { id: 'international', label: t('pages.programs.sector_international'), color: '#2E7A82', icon: 'fas fa-globe-americas' },
  { id: 'technology',    label: t('pages.programs.sector_technology'),    color: '#C97A2E', icon: 'fas fa-microchip' },
  { id: 'medical',       label: t('pages.programs.sector_medical'),       color: '#B5834A', icon: 'fas fa-heartbeat' },
  { id: 'education',     label: t('pages.programs.sector_education'),     color: '#4F8F6A', icon: 'fas fa-graduation-cap' },
  { id: 'business',      label: t('pages.programs.sector_business'),      color: '#A6763A', icon: 'fas fa-briefcase' },
  { id: 'arts',          label: t('pages.programs.sector_arts'),          color: '#D99A3D', icon: 'fas fa-palette' },
  { id: 'science',       label: t('pages.programs.sector_science'),       color: '#3D6B78', icon: 'fas fa-flask' },
  { id: 'sports',        label: t('pages.programs.sector_sports'),        color: '#8F6A46', icon: 'fas fa-running' },
  { id: 'agriculture',   label: t('pages.programs.sector_agriculture'),   color: '#6B8F4E', icon: 'fas fa-tractor' },
  { id: 'local',         label: t('pages.programs.sector_local'),         color: '#5FA184', icon: 'fas fa-map-marker-alt' },
  { id: 'media',         label: t('pages.programs.sector_media'),         color: '#3E7A5C', icon: 'fas fa-photo-video' },
];

const getDefaultCategories = (t) => [
  { id: 'all',          label: t('pages.programs.allPrograms'),   icon: 'fas fa-list',            color: '#7C8C83', sector: null,            subcategories: [] },
  { id: 'education',    label: t('pages.programs.cat_education'), icon: 'fas fa-graduation-cap',  color: '#4F8F6A', sector: 'education',     subcategories: [{ id: 'all_education', label: t('pages.programs.sub_all_education'), icon: 'fas fa-book-open' },{ id: 'abroad', label: t('pages.programs.sub_abroad'), icon: 'fas fa-plane' },{ id: 'bachelor', label: t('pages.programs.sub_bachelor'), icon: 'fas fa-user-graduate' },{ id: 'master', label: t('pages.programs.sub_master'), icon: 'fas fa-user-graduate' },{ id: 'phd', label: t('pages.programs.sub_phd'), icon: 'fas fa-user-graduate' },{ id: 'online', label: t('pages.programs.sub_online'), icon: 'fas fa-laptop' }] },
  { id: 'scholarship',  label: t('pages.programs.cat_scholarship'), icon: 'fas fa-money-bill-wave', color: '#2E7A82', sector: 'education',     subcategories: [{ id: 'all_scholarship', label: t('pages.programs.sub_all_scholarship'), icon: 'fas fa-award' },{ id: 'full', label: t('pages.programs.sub_full'), icon: 'fas fa-star' },{ id: 'partial', label: t('pages.programs.sub_partial'), icon: 'fas fa-star-half-alt' },{ id: 'government', label: t('pages.programs.sub_government'), icon: 'fas fa-landmark' },{ id: 'private', label: t('pages.programs.sub_private'), icon: 'fas fa-building' }] },
  { id: 'fellowship',   label: t('pages.programs.cat_fellowship'), icon: 'fas fa-handshake',       color: '#D99A3D', sector: 'science',       subcategories: [{ id: 'all_fellowship', label: t('pages.programs.sub_all_fellowship'), icon: 'fas fa-users' },{ id: 'research', label: t('pages.programs.sub_research'), icon: 'fas fa-microscope' },{ id: 'postdoc', label: t('pages.programs.sub_postdoc'), icon: 'fas fa-user-md' },{ id: 'visiting', label: t('pages.programs.sub_visiting'), icon: 'fas fa-plane-departure' }] },
  { id: 'erasmusplus',  label: t('pages.programs.cat_erasmusplus'), icon: 'fas fa-plane',           color: '#3D6B78', sector: 'international', subcategories: [{ id: 'all_erasmusplus', label: t('pages.programs.sub_all_erasmusplus'), icon: 'fas fa-plus-circle' },{ id: 'ka1', label: t('pages.programs.sub_ka1'), icon: 'fas fa-exchange-alt' },{ id: 'ka2', label: t('pages.programs.sub_ka2'), icon: 'fas fa-handshake' },{ id: 'ka3', label: t('pages.programs.sub_ka3'), icon: 'fas fa-cogs' }] },
  { id: 'training',     label: t('pages.programs.cat_training'), icon: 'fas fa-book',            color: '#C97A2E', sector: 'technology',    subcategories: [{ id: 'all_training', label: t('pages.programs.sub_all_training'), icon: 'fas fa-dumbbell' },{ id: 'professional', label: t('pages.programs.sub_professional'), icon: 'fas fa-briefcase' },{ id: 'technical', label: t('pages.programs.sub_technical'), icon: 'fas fa-tools' },{ id: 'soft_skills', label: t('pages.programs.sub_soft_skills'), icon: 'fas fa-comments' },{ id: 'online_training', label: t('pages.programs.sub_online_training'), icon: 'fas fa-laptop-code' }] },
  { id: 'grant',        label: t('pages.programs.cat_grant'), icon: 'fas fa-dollar-sign',     color: '#A6763A', sector: 'business',      subcategories: [{ id: 'all_grant', label: t('pages.programs.sub_all_grant'), icon: 'fas fa-hand-holding-usd' },{ id: 'research_grant', label: t('pages.programs.sub_research_grant'), icon: 'fas fa-flask' },{ id: 'project_grant', label: t('pages.programs.sub_project_grant'), icon: 'fas fa-project-diagram' },{ id: 'travel_grant', label: t('pages.programs.sub_travel_grant'), icon: 'fas fa-plane' }] },
  { id: 'internship',   label: t('pages.programs.cat_internship'), icon: 'fas fa-user-tie',        color: '#8F6A46', sector: 'business',      subcategories: [{ id: 'all_internship', label: t('pages.programs.sub_all_internship'), icon: 'fas fa-briefcase' },{ id: 'summer', label: t('pages.programs.sub_summer'), icon: 'fas fa-sun' },{ id: 'paid', label: t('pages.programs.sub_paid'), icon: 'fas fa-money-bill-wave' },{ id: 'unpaid', label: t('pages.programs.sub_unpaid'), icon: 'fas fa-hand-holding-heart' },{ id: 'virtual', label: t('pages.programs.sub_virtual'), icon: 'fas fa-laptop-house' }] },
  { id: 'conference',   label: t('pages.programs.cat_conference'), icon: 'fas fa-comments',        color: '#3E7A5C', sector: 'arts',          subcategories: [{ id: 'all_conference', label: t('pages.programs.sub_all_conference'), icon: 'fas fa-comment-alt' },{ id: 'international_conf', label: t('pages.programs.sub_international_conf'), icon: 'fas fa-globe' },{ id: 'funded_conf', label: t('pages.programs.sub_funded_conf'), icon: 'fas fa-money-bill' },{ id: 'virtual_conf', label: t('pages.programs.sub_virtual_conf'), icon: 'fas fa-video' }] },
  { id: 'online',       label: t('pages.programs.cat_online'), icon: 'fas fa-laptop',          color: '#5FA184', sector: 'technology',    subcategories: [{ id: 'all_online', label: t('pages.programs.sub_all_online'), icon: 'fas fa-globe' },{ id: 'online_course', label: t('pages.programs.sub_online_course'), icon: 'fas fa-book-open' },{ id: 'webinar', label: t('pages.programs.sub_webinar'), icon: 'fas fa-video' },{ id: 'mooc', label: t('pages.programs.sub_mooc'), icon: 'fas fa-laptop-code' }] },
  { id: 'volunteering', label: t('pages.programs.cat_volunteering'), icon: 'fas fa-hands-helping',   color: '#2E6B52', sector: 'international', subcategories: [{ id: 'all_volunteering', label: t('pages.programs.sub_all_volunteering'), icon: 'fas fa-heart' },{ id: 'local_vol', label: t('pages.programs.sub_local_vol'), icon: 'fas fa-map-marker-alt' },{ id: 'international_vol', label: t('pages.programs.sub_international_vol'), icon: 'fas fa-globe' },{ id: 'evs', label: t('pages.programs.sub_evs'), icon: 'fas fa-star' }] },
  { id: 'event',        label: t('pages.programs.cat_event'), icon: 'fas fa-calendar-alt',    color: '#B5834A', sector: 'arts',          subcategories: [{ id: 'all_event', label: t('pages.programs.sub_all_event'), icon: 'fas fa-calendar' },{ id: 'festival', label: t('pages.programs.sub_festival'), icon: 'fas fa-music' },{ id: 'competition', label: t('pages.programs.sub_competition'), icon: 'fas fa-trophy' },{ id: 'summit', label: t('pages.programs.sub_summit'), icon: 'fas fa-users' }] },
  { id: 'award',        label: t('pages.programs.cat_award'), icon: 'fas fa-trophy',          color: '#6B8F4E', sector: 'arts',          subcategories: [{ id: 'all_award', label: t('pages.programs.sub_all_award'), icon: 'fas fa-award' },{ id: 'international_award', label: t('pages.programs.sub_international_award'), icon: 'fas fa-globe' },{ id: 'national_award', label: t('pages.programs.sub_national_award'), icon: 'fas fa-flag' },{ id: 'regional_award', label: t('pages.programs.sub_regional_award'), icon: 'fas fa-map-pin' },{ id: 'special_award', label: t('pages.programs.sub_special_award'), icon: 'fas fa-star-of-life' }] },
];

function loadAdminSettings() {
  try { const s = localStorage.getItem('admin_program_settings'); if (s) return JSON.parse(s); } catch {}
  return null;
}

// ── Parse deadline string into a Date object ──
// Handles: ISO, Armenian month names + "ժամը՝ HH:MM" / "HH։MM" / "HH:MM"
function parseDeadlineDate(value) {
  const str = String(value || '').trim();
  if (!str) return null;

  // Step 1: Extract time BEFORE normalizing (covers "ժամը՝ 17:00", "17։00", "17:00")
  // Pattern: optional "ժամ[ը][՝: ]" then HH:MM or HH։MM
  const timeMatch = str.match(/(?:ժամ[ըը]?(?:[՝:]\s*|\s+))?([01]?\d|2[0-3])\s*[։:]\s*([0-5]\d)\b/i);
  const hh = timeMatch ? parseInt(timeMatch[1], 10) : null;
  const mm = timeMatch ? parseInt(timeMatch[2], 10) : null;
  const hasTime = hh !== null && mm !== null;

  // Step 2: Normalize string for date parsing
  const normalized = str
    .replace(/[։]/g, ':')
    .replace(/ժամ[ըը]?(?:[՝:]\s*|\s+)\d{1,2}:\d{2}/gi, '') // strip time phrase
    .replace(/ժամ[ըը]?\s*/gi, '')
    .replace(/\bթ\.?\b/gi, '')  // strip Armenian year suffix "թ."
    .replace(/[՝՛]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Step 3: Try native Date parse on normalized
  const direct = new Date(normalized);
  if (!isNaN(direct.getTime()) && direct.getFullYear() > 2000) {
    if (hasTime) direct.setHours(hh, mm, 0, 0);
    return direct;
  }

  // Step 4: Armenian month names. Matched by their bare stem (dropping the
  // genitive "ի" suffix) so both "ապրիլի 19-ին" (genitive, "of April") and
  // the equally common bare nominative "19 ապրիլ 2026" (no suffix) match —
  // requiring the full genitive form silently failed to parse dates written
  // in the nominative, which made formatDeadline() fall back to
  // `expired: false` and left already-passed programs stuck out of the
  // archive forever.
  const amMonths = {
    հունվար:0, փետրվար:1, մարտ:2, ապրիլ:3, մայիս:4, հունիս:5,
    հուլիս:6, օգոստոս:7, սեպտեմբեր:8, հոկտեմբեր:9, նոյեմբեր:10, դեկտեմբեր:11,
  };
  const lower = normalized.toLowerCase();
  const yearM = lower.match(/\b(20\d{2})\b/);
  // find day digit that isn't the year
  const noYear = lower.replace(/20\d{2}/, '####');
  const dayM   = noYear.match(/\b([0-2]?\d|3[01])\b/);
  let month = null;
  for (const [name, idx] of Object.entries(amMonths)) {
    if (lower.includes(name)) { month = idx; break; }
  }
  if (month !== null && dayM) {
    const year = yearM ? parseInt(yearM[1], 10) : new Date().getFullYear();
    const d = new Date(year, month, parseInt(dayM[1], 10), hasTime ? hh : 23, hasTime ? mm : 59, 0);
    if (!isNaN(d.getTime())) return d;
  }

  // Step 5: English month fallback
  const enMonths = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11,
    january:0,february:1,march:2,april:3,june:5,july:6,august:7,september:8,october:9,november:10,december:11 };
  for (const [name, idx] of Object.entries(enMonths)) {
    if (lower.includes(name)) {
      const yearE = lower.match(/\b(20\d{2})\b/);
      const dayE  = lower.replace(name,'').match(/\b(\d{1,2})\b/);
      if (dayE) {
        const year = yearE ? parseInt(yearE[1]) : new Date().getFullYear();
        const d = new Date(year, idx, parseInt(dayE[1]), hasTime ? hh : 23, hasTime ? mm : 59, 0);
        if (!isNaN(d.getTime())) return d;
      }
    }
  }
  return null;
}

// ── Build display label for deadline badge ──
// Keeps original Armenian text AS-IS (including "Գրանցման վերջնաժամկետ՝ ...").
// Only strips English-only "Deadline:" prefixes.
function getDeadlineLabel(originalStr, parsedDate, hasTime) {
  const str = String(originalStr || '').trim();
  // Strip ONLY English deadline prefixes
  const stripped = str
    .replace(/^(?:application\s+)?deadline\s*[:：\-]?\s*/i, '')
    .replace(/^apply\s+by\s*[:：\-]?\s*/i, '')
    .replace(/^closing\s+date\s*[:：\-]?\s*/i, '')
    .trim();
  if (stripped) return stripped;
  // Pure fallback
  if (parsedDate) {
    const ds = parsedDate.toLocaleDateString('hy-AM', { day:'numeric', month:'long', year:'numeric' });
    if (hasTime) {
      const H = String(parsedDate.getHours()).padStart(2,'0');
      const M = String(parsedDate.getMinutes()).padStart(2,'0');
      return `${ds}, ժամը ${H}:${M}`;
    }
    return ds;
  }
  return str;
}

// ── Main formatDeadline ──
function formatDeadline(deadline) {
  if (!deadline) return null;
  const str = String(deadline).trim();
  if (!str || str === 'null' || str === 'undefined') return null;
  const now    = new Date();
  const hasTime = /\b\d{1,2}\s*[։:]\s*\d{2}\b/.test(str) || /ժամ/i.test(str) || /\b(?:am|pm)\b/i.test(str);
  const parsed  = parseDeadlineDate(str);
  if (!parsed) {
    const label = getDeadlineLabel(str, null, false);
    return label ? { formatted: label, expired: false, near: false, date: null } : null;
  }
  const diffMs = parsed.getTime() - now.getTime();
  const near   = diffMs >= 0 && diffMs <= 24 * 60 * 60 * 1000;
  const label  = getDeadlineLabel(str, parsed, hasTime);
  return { formatted: label, expired: parsed < now, near, date: parsed };
}

// Is deadline within 1 day (but not yet expired)?
function isDeadlineSoon(deadlineInfo) {
  if (!deadlineInfo) return false;
  return !!deadlineInfo.near;
}

// A program only moves to the archive once a full day has passed since its date
// expired — a badge can show "expired" right away, but archiving waits out a
// 1-day grace window so programs don't vanish from the active list the instant
// the clock ticks over.
const ARCHIVE_GRACE_MS = 24 * 60 * 60 * 1000;
function isPastArchiveGrace(dateInfo) {
  if (!dateInfo?.expired || !dateInfo.date) return false;
  return (Date.now() - dateInfo.date.getTime()) >= ARCHIVE_GRACE_MS;
}

const NO_DEADLINE_PATTERNS = [
  'no deadline',
  'deadline not specified',
  'no application deadline',
  'no fixed deadline',
  'registration not required',
  'no registration required',
  'until the event day',
  'until event day',
  'until meeting day',
  'վերջնաժամկետ չկա',
  'վերջնաժամկետ նշված չէ',
  'գրանցում չի պահանջվում',
  'մինչև հանդիպման օրը',
  'մինչեւ հանդիպման օրը',
];

// Armenian (both cases) or Latin/digit "letter" — used to make sure a marker
// match ends at a real word boundary instead of a prefix of a longer word.
function isWordChar(ch) {
  return /[A-Za-z0-9Ա-Ֆա-և]/.test(ch);
}

function hasNoDeadlineSignal(value) {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return false;
  return NO_DEADLINE_PATTERNS.some((pattern) => text.includes(pattern));
}

// Both marker lists below include the bare Armenian noun stem plus its
// common suffixed forms (-ը definite article, -ներ plural, -ները plural +
// definite) as separate literal entries — Armenian text almost never uses
// the bare stem on its own ("ժամկետներ" far outnumbers "ժամկետ"), and the
// word-boundary check in extractDateCandidateFromText() rejects a stem match
// that's actually the prefix of a longer, differently-suffixed word.
const DEADLINE_MARKERS = [
  'deadline',
  'application deadline',
  'apply by',
  'closing date',
  'registration deadline',
  'գրանցման վերջնաժամկետ', 'գրանցման վերջնաժամկետը',
  'դիմումների վերջնաժամկետ', 'դիմումների վերջնաժամկետը',
  'վերջնաժամկետ', 'վերջնաժամկետը', 'վերջնաժամկետներ',
];

// Markers for the event's own date ("Անցկացման ժամկետներ", "when it takes
// place") — distinct from the application deadline above. Many posts only
// ever mention this date inside the free-text description with no
// structured start_date/end_date field, so it needs the same marker-based
// extraction the deadline gets, or a program with a long-past event date but
// an open/unspecified deadline never archives.
const EVENT_DATE_MARKERS = [
  'անցկացման ժամկետներ', 'անցկացման ժամկետը', 'անցկացման ժամկետ',
  'իրականացման ժամկետներ', 'իրականացման ժամկետը', 'իրականացման ժամկետ',
  'տեղի կունենա',
  'event date',
  'takes place',
];

function extractGenericDateCandidate(text) {
  if (!text) return '';
  const raw = String(text);
  const longDate = raw.match(/\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|sept|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+\d{1,2},?\s+20\d{2}\b/i);
  if (longDate) return longDate[0];
  const numeric = raw.match(/\b\d{1,2}[\/.-]\d{1,2}[\/.-](?:20)?\d{2}\b/);
  if (numeric) return numeric[0];
  return '';
}

function Programs() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { slugId } = useParams();
  // Supports both the new crawlable /programs/:slug-<id> path and the old
  // ?program=<id> query param (old shared/bookmarked links keep working).
  const routeProgramId = slugId ? extractProgramIdFromSlug(slugId) : searchParams.get('program');
  const { user: authUser, loading: authLoading, makeApiCall } = useAuth();
  const currentUser = authUser || JSON.parse(localStorage.getItem('user') || 'null');
  const isAuthenticated = !!currentUser;

  const isAdmin = currentUser?.role === 'admin' || (currentUser?.role === 'secondary_admin' && currentUser?.secondaryAdminApproved);
  const isOrg   = currentUser?.accountType === 'organization' || currentUser?.userType === 'organizer';
  const previewLimit = 10;

  const adminSettings = loadAdminSettings();
  const mergeById = (defaults = [], saved = []) => {
    const dmap = new Map(defaults.map(x => [x.id, x]));
    const smap = new Map(saved.map(x => [x.id, x]));
    const ids = new Set([...dmap.keys(), ...smap.keys()]);
    return Array.from(ids).map(id => {
      const d = dmap.get(id) || { id };
      const s = smap.get(id) || {};
      return {
        ...d,
        ...s,
        subcategories: Array.isArray(s.subcategories) && s.subcategories.length > 0 ? s.subcategories : (d.subcategories || []),
      };
    });
  };

  const sectors    = mergeById(getDefaultSectors(t), adminSettings?.sectors || []);
  const categories = mergeById(getDefaultCategories(t), adminSettings?.categories || []);
  const sourceLabels = {
    ai: t('pages.programs.sourceByAi'),
    admin: t('pages.programs.sourceByAdmin'),
    organization: t('pages.programs.sourceByOrganization'),
  };

  const [programs,            setPrograms]            = useState([]);
  const [filteredPrograms,    setFilteredPrograms]    = useState([]);
  const [displayedPrograms,   setDisplayedPrograms]   = useState([]);
  const [visibleProgramCount, setVisibleProgramCount]  = useState(PROGRAMS_BATCH_SIZE);
  const [loading,             setLoading]             = useState(true);
  const [selectedCategory,    setSelectedCategory]    = useState(() => searchParams.get('category') || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [expandedCategories,  setExpandedCategories]  = useState(new Set());
  const [selectedSector,      setSelectedSector]      = useState(null);
  const [sourceFilter,        setSourceFilter]        = useState('all');
  const [greencoinsOnly,      setGreencoinsOnly]      = useState(false);
  const [selectedProgram,     setSelectedProgram]     = useState(null);
  const [isModalOpen,         setIsModalOpen]         = useState(false);
  const [ageFilter,           setAgeFilter]           = useState('');
  const [educationLevelFilter, setEducationLevelFilter] = useState('');
  // NEW: deadline range filter
  const [deadlineFilter,      setDeadlineFilter]      = useState('');
  const [showArchive,         setShowArchive]         = useState(false);
  const [actionLoading,       setActionLoading]       = useState({});
  const [editingId,           setEditingId]           = useState(null);
  const [editForm,            setEditForm]            = useState({});
  const [deleteConfirm,       setDeleteConfirm]       = useState(null);
  const [toast,               setToast]               = useState('');
  const [sidebarOpen,         setSidebarOpen]         = useState(false);
  const [nearPageEnd,         setNearPageEnd]         = useState(false);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [aiRecsMode,          setAiRecsMode]          = useState(false);
  const [aiRecommendations,   setAiRecommendations]   = useState([]);
  const [aiRecsLoading,       setAiRecsLoading]       = useState(false);
  const [aiRecsError,         setAiRecsError]         = useState('');
  const [aiRecsCached,        setAiRecsCached]        = useState(false);
  const [aiRecsNextAvailableAt, setAiRecsNextAvailableAt] = useState(null);
  const [aiThinkingPercent,   setAiThinkingPercent]   = useState(0);
  const [aiThinkingStatus,    setAiThinkingStatus]    = useState(() => t('pages.programs.aiPhaseReading'));
  const [savedProgramIds,     setSavedProgramIds]     = useState(new Set());
  const [applicationFormInfo, setApplicationFormInfo] = useState(null); // { applicationForm, alreadyApplied, myApplication }
  const [showApplyFormModal,  setShowApplyFormModal]  = useState(false);
  const [reviewRating,        setReviewRating]        = useState(0);
  const [reviewHoverRating,   setReviewHoverRating]   = useState(0);
  const [reviewComment,       setReviewComment]       = useState('');
  const [reviewSubmitting,    setReviewSubmitting]    = useState(false);
  const [reviewSubmitted,     setReviewSubmitted]     = useState(false);

  const modalRef           = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || `${GREENWICH_BACKEND_URL}/api`;

  const trackApplyClick = (programId) => {
    if (!programId) return;
    fetch(`${API_BASE}/programs/${programId}/apply-click`, { method: 'POST' }).catch(() => {});
  };
  const trackProgramView = (programId) => {
    if (!programId) return;
    fetch(`${API_BASE}/programs/${programId}/view`, { method: 'POST' }).catch(() => {});
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // ── Save / bookmark toggle ──
  const loadSavedProgramIds = async () => {
    if (!isAuthenticated || !makeApiCall) return;
    try {
      const res = await makeApiCall('/api/programs/saved');
      if (res.success && Array.isArray(res.programs)) {
        setSavedProgramIds(new Set(res.programs.map(p => String(p.id || p._id || p.programId))));
      }
    } catch {
      // best-effort; ignore failures
    }
  };

  const toggleSaveProgram = async (e, program) => {
    if (e) e.stopPropagation();
    const id = String(program.id || program._id || '');
    if (!id) return;

    if (!isAuthenticated) {
      setShowRegistrationPrompt(true);
      return;
    }

    const wasSaved = savedProgramIds.has(id);
    // Optimistic toggle
    setSavedProgramIds(prev => {
      const next = new Set(prev);
      if (wasSaved) next.delete(id); else next.add(id);
      return next;
    });

    try {
      const res = await makeApiCall(`/api/programs/${id}/save`, { method: 'POST' });
      if (res.success) {
        setSavedProgramIds(prev => {
          const next = new Set(prev);
          if (res.saved) next.add(id); else next.delete(id);
          return next;
        });
      } else {
        // revert on failure
        setSavedProgramIds(prev => {
          const next = new Set(prev);
          if (wasSaved) next.add(id); else next.delete(id);
          return next;
        });
      }
    } catch {
      setSavedProgramIds(prev => {
        const next = new Set(prev);
        if (wasSaved) next.add(id); else next.delete(id);
        return next;
      });
    }
  };

  const ageOptions = useMemo(() => [
    { value:'', label: t('pages.programs.anyAge') },
    { value:'18-25', label: t('pages.programs.age18_25') },
    { value:'25-35', label: t('pages.programs.age25_35') },
    { value:'35+', label: t('pages.programs.age35plus') },
  ], [t]);

  const educationLevelOptions = useMemo(() => [
    { value: '', label: t('pages.programs.educationAny') },
    { value: 'undergraduate', label: t('pages.programs.educationUndergraduate') },
    { value: 'graduate', label: t('pages.programs.educationGraduate') },
    { value: 'doctoral', label: t('pages.programs.educationDoctoral') },
    { value: 'student', label: t('pages.programs.educationStudent') },
    { value: 'professional', label: t('pages.programs.educationProfessional') },
    { value: 'high_school', label: t('pages.programs.educationHighSchool') },
  ], [t]);

  // NEW: Deadline filter options (in days)
  const deadlineOptions = useMemo(() => [
    { value: '',   label: t('pages.programs.deadlineAny') },
    { value: '1',  label: t('pages.programs.deadlineLast24h') },
    { value: '3',  label: t('pages.programs.deadlineIn3Days') },
    { value: '7',  label: t('pages.programs.deadlineIn7Days') },
    { value: '30', label: t('pages.programs.deadlineIn30Days') },
  ], [t]);

  const loadPrograms = async () => {
    setLoading(true);

    const loadFromEndpoint = async (url, options = {}) => {
      const { headers, ...normalizeOptions } = options;
      const response = await fetchWithTimeout(url, headers ? { headers } : {});
      if (!response.ok) throw new Error(`Programs request failed: ${response.status}`);
      const payload = await response.json();
      return normalizeFetchedPrograms(payload, normalizeOptions);
    };

    try {
      try {
        // Content language (which language programs are shown in) is independent of the
        // interface language — Settings lets a user pick "UI in Russian, content in English".
        // Falls back to the interface language until they explicitly set one.
        const langParam = (localStorage.getItem('contentLanguage') || i18n.language || 'en').split('-')[0];
        const token = localStorage.getItem('token');
        const dbPrograms = await loadFromEndpoint(`${API_BASE}/programs/approved?lang=${langParam}&market=${MARKET}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
        if (dbPrograms.length > 0) {
          setPrograms(dbPrograms);
          return;
        }
      } catch (error) {
        console.warn('Approved programs endpoint unavailable:', error);
      }

      try {
        const saved = localStorage.getItem('admin_programs');
        if (saved) {
          const localPrograms = normalizeFetchedPrograms(JSON.parse(saved));
          const approved = localPrograms.filter((p) => p.approval_status === 'approved' || p.status === 'approved');
          if (approved.length > 0) {
            setPrograms(approved);
            return;
          }
        }
      } catch (error) {
        console.warn('Saved programs could not be loaded:', error);
      }

      const fallbackSources = [
        { url: `${API_BASE}/scraper-proxy/programs`, source: 'ai' },
        { url: `${API_BASE}/programs/fallback`, source: 'fallback' },
      ];

      for (const fallback of fallbackSources) {
        try {
          const fallbackPrograms = await loadFromEndpoint(fallback.url, {
            markApproved: true,
            source: fallback.source,
          });
          if (fallbackPrograms.length > 0) {
            setPrograms(fallbackPrograms);
            return;
          }
        } catch (error) {
          console.warn(`Programs fallback failed (${fallback.url}):`, error);
        }
      }

      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  const doStatusChange = async (id, action) => {
    setActionLoading(p => ({ ...p, [id]: action }));
    try {
      const token = localStorage.getItem('token');
      const slug  = action === 'approve' ? 'approve' : 'reject';
      const prog  = programs.find(p => String(p.id || p._id) === String(id));
      let res     = await fetch(`${API_BASE}/programs/${id}/${slug}`, { method:'PATCH', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' } });
      if (!res.ok && prog?.programId) {
        res = await fetch(`${API_BASE}/programs/approve`, {
          method: 'POST',
          headers: { Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
          body: JSON.stringify({
            programId: prog.programId,
            status: action === 'approve' ? 'approved' : 'rejected',
            approved_via: String(prog.programId || '').startsWith('org-') ? 'organization' : undefined,
          }),
        });
      }
      if (res.ok) { showToast(action === 'approve' ? '✅ Approved!' : '❌ Rejected/Revoked.'); await loadPrograms(); }
      else showToast('❌ Failed to update status');
    } catch { showToast('❌ Error updating status'); }
    setActionLoading(p => ({ ...p, [id]: null }));
  };

  const doDelete = async (id) => {
    setActionLoading(p => ({ ...p, [id]: 'delete' }));
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_BASE}/programs/${id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
      if (res.ok) { showToast('🗑 Deleted.'); setDeleteConfirm(null); loadPrograms(); }
    } catch {}
    setActionLoading(p => ({ ...p, [id]: null }));
  };

  const saveEdit = async (id) => {
    setActionLoading(p => ({ ...p, [id]: 'edit' }));
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_BASE}/programs/${id}`, { method:'PUT', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' }, body:JSON.stringify(editForm) });
      if (res.ok) { showToast('✏️ Updated!'); setEditingId(null); loadPrograms(); }
    } catch {}
    setActionLoading(p => ({ ...p, [id]: null }));
  };

  const extractAgeFromText = (text) => {
    if (!text) return '';
    const lower    = text.toLowerCase();
    const patterns = [/(\d{1,2})\s*[-–—]\s*(\d{1,2})/,/(\d{1,2})\s*to\s*(\d{1,2})/,/between\s*(\d{1,2})\s*and\s*(\d{1,2})/,/(\d{1,2})\s*\+/,/(over|above)\s*(\d{1,2})/,/(under|below)\s*(\d{1,2})/,/(\d{1,2})\s*(?:years?|yrs?)/,/(\d{1,2})-year-?old/,/at\s*least\s*(\d{1,2})/];
    for (const pattern of patterns) {
      const match = lower.match(pattern);
      if (match) {
        if (match.length >= 3 && match[1] && match[2]) return `${parseInt(match[1])}-${parseInt(match[2])}`;
        if (match[1] && lower.includes('+')) return `${parseInt(match[1])}+`;
        if (match[1]) return `${parseInt(match[1])}`;
      }
    }
    return '';
  };

  const matchesAgeFilter = (programAge, filterValue) => {
    if (!filterValue) return true;
    if (!programAge) return false;
    const ageStr       = programAge.toString();
    const numbers      = ageStr.match(/\d+/g) || [];
    if (numbers.length === 0) return false;
    const filterNumbers = filterValue.match(/\d+/g);
    if (!filterNumbers) return false;
    const filterMin = parseInt(filterNumbers[0], 10);
    const filterMax = filterNumbers.length > 1 ? parseInt(filterNumbers[1], 10) : filterMin;
    for (const num of numbers) {
      const age = parseInt(num, 10);
      if (ageStr.includes('+') && age <= filterMax) return true;
      if (ageStr.includes('-') && numbers.length >= 2) {
        const pMin = parseInt(numbers[0], 10);
        const pMax = parseInt(numbers[1], 10);
        if (pMin <= filterMax && pMax >= filterMin) return true;
      }
      if (age >= filterMin && age <= filterMax) return true;
    }
    return false;
  };

  const matchesEducationLevel = (text, filterValue) => {
    if (!filterValue) return true;
    const t = `${text || ''}`.toLowerCase();
    switch (filterValue) {
      case 'undergraduate':
        return /\b(undergraduate|undergrad|bachelor|bsc|b\.sc\.?|ba\b|b\.a\.?|first cycle|assoc)/i.test(t);
      case 'graduate':
        if (/\b(phd|doctorate|doctoral|postdoc)\b/i.test(t)) return false;
        return /\b(graduate\s+student|graduate\s+program|master'?s?|\bm\.?sc\.?\b|\bm\.?a\.?\b|\bmba\b|second cycle|postgraduate\s+diploma)\b/i.test(t);
      case 'doctoral':
        return /\b(phd|ph\.d|doctorate|doctoral|postdoc|post-doctoral|d\.phil)\b/i.test(t);
      case 'student':
        return /\bstudent\b/i.test(t)
          || /\b(undergraduate|bachelor|master'?s?|phd|doctoral)\b/i.test(t);
      case 'professional':
        return /\b(professional|early career|career development|mid-career|working professional)\b/i.test(t);
      case 'high_school':
        return /\b(high school|secondary school|lyceum|k-12|grade\s1[01])\b/i.test(t);
      default:
        return true;
    }
  };

  // "Not specified" is a valid, common answer for a field (no deadline given,
  // no fixed event date yet) — it must never be treated as a parseable date
  // candidate, or every downstream date check just wastes time failing on it.
  const isUnspecifiedValue = (value) => {
    const v = String(value || '').trim().toLowerCase();
    if (!v) return true;
    return ['նշված չէ', 'not specified', 'n/a', 'na', 'tbd', 'to be determined', 'unspecified'].includes(v);
  };

  const extractDateCandidateFromText = (text, markers = DEADLINE_MARKERS) => {
    if (!text) return '';
    const raw = String(text);
    const lower = raw.toLowerCase();
    for (const marker of markers) {
      // A marker like "անցկացման ժամկետ" is itself a prefix of the longer word
      // "ժամկետներ" ("...-ներ" plural suffix), so a plain indexOf() can match
      // mid-word and truncate the real text into garbage (leaves "ներ՝" as the
      // "value"). Keep scanning past any match where the next character
      // continues a word instead of ending the marker.
      let idx = -1;
      let searchFrom = 0;
      while (searchFrom <= lower.length) {
        const found = lower.indexOf(marker, searchFrom);
        if (found === -1) break;
        const nextChar = lower[found + marker.length] || '';
        if (!isWordChar(nextChar)) { idx = found; break; }
        searchFrom = found + marker.length;
      }
      if (idx >= 0) {
        const tail = raw.slice(idx, idx + 120);
        const tailLines = tail.split('\n');
        const line = tailLines[0] || tail;
        // Skip the marker text itself, then skip leading separators (՝ : – — space)
        // Do NOT use new RegExp (crashes with Armenian chars in character class ranges)
        // Do NOT use greedy .*?: (eats HH:MM colons)
        const afterMarker = line.slice(marker.length);
        const after = afterMarker.replace(/^[\s\u055A\u055B\u055D\u02BC\u2018\u2019\u0060'`：:：\-\u2013\u2014]+/, '').trim();
        if (after && !isUnspecifiedValue(after)) return after;
        // The label and its value are sometimes on separate lines
        // ("Deadline:\n2026 May 15..."), so fall back to the next non-empty line.
        const nextLine = (tailLines[1] || '').trim();
        if (nextLine && !isUnspecifiedValue(nextLine)) return nextLine;
      }
    }
    const longDate = raw.match(/\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|sept|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+\d{1,2},?\s+20\d{2}\b/i);
    if (longDate) return longDate[0];
    const numeric = raw.match(/\b\d{1,2}[\/.-]\d{1,2}[\/.-](?:20)?\d{2}\b/);
    if (numeric) return numeric[0];
    return '';
  };

  const getProgramTimeline = (program) => {
    const rawDeadline = String(program?.deadline || '').trim();
    const rawStart = String(program?.start_date || '').trim();
    const rawEnd = String(program?.end_date || '').trim();
    // The Telegram bot sometimes stores just the field's label ("Անցկացման
    // ժամկետներ՝") into start_date/end_date instead of the actual date, when
    // the real value sat on the next line of the post. That garbage is
    // non-empty, so it silently defeated every "is this field filled in?"
    // check below — only trust a structured field once it actually parses.
    const explicitStart = parseDeadlineDate(rawStart) ? rawStart : '';
    const explicitEnd = parseDeadlineDate(rawEnd) ? rawEnd : '';
    const explicitDeadline = rawDeadline;
    const text = `${program?.title || ''} ${program?.description || ''} ${program?.full_text || ''}`;
    const deadlineIsUnavailable = hasNoDeadlineSignal(explicitDeadline) || hasNoDeadlineSignal(text);
    let value = deadlineIsUnavailable ? '' : explicitDeadline;

    // Event date (срок проведения) — the day the program/event itself takes place.
    // Tracked independently of the application deadline via the explicit start_date
    // or end_date field (in practice most scraped/admin-entered programs only fill
    // in end_date), so a past event date archives the program on its own even when
    // the deadline is still open or unspecified. When neither field is filled in
    // (or was garbage, per the label-only bug above), fall back to the "Անցկացման
    // ժամկետներ" line in the free-text description — very common for manually
    // entered posts that never got a valid structured date.
    let eventDateSource = explicitStart || explicitEnd;
    if (!eventDateSource) {
      const candidate = extractDateCandidateFromText(text, EVENT_DATE_MARKERS);
      if (candidate && !isUnspecifiedValue(candidate)) eventDateSource = candidate;
    }
    const eventInfo = eventDateSource ? formatDeadline(eventDateSource) : null;

    if (!value && !deadlineIsUnavailable) {
      value = extractDateCandidateFromText(text);
    }

    let kind = 'deadline';
    if (!value && explicitStart) {
      value = explicitStart;
      kind = 'start_date';
    }
    if (!value && explicitEnd) {
      value = explicitEnd;
      kind = 'end_date';
    }
    if (!value) {
      value = extractGenericDateCandidate(text);
      kind = value ? 'event_date' : 'none';
    }
    if (!value) {
      const fallbackText = `${explicitDeadline} ${text}`;
      value = extractGenericDateCandidate(fallbackText);
      kind = value ? 'event_date' : 'none';
    }
    if (!value) {
      if (deadlineIsUnavailable) return { info: null, eventInfo, archived: isPastArchiveGrace(eventInfo), kind: 'no_deadline' };
      const text = `${program?.title || ''} ${program?.description || ''} ${program?.full_text || ''}`;
      value = extractDateCandidateFromText(text);
      kind = value ? 'deadline' : 'none';
    }
    if (!value) return { info: null, eventInfo, archived: isPastArchiveGrace(eventInfo), kind: 'none' };
    const info = formatDeadline(value);
    const archiveByKind = kind === 'deadline' || kind === 'end_date' || kind === 'event_date';
    const archived = (archiveByKind && isPastArchiveGrace(info)) || isPastArchiveGrace(eventInfo);
    return { info, eventInfo, archived, kind };
  };

  const inferProgramSector = (program) => {
    const text = `${program.title||''} ${program.description||''} ${program.full_text||''}`.toLowerCase();
    const cats  = (program.categories || []).map(c => c.toLowerCase());
    if (text.includes('local')||text.includes('community')||cats.includes('local')) return 'local';
    if (text.includes('ecology')||text.includes('environment')||text.includes('climate')||cats.includes('environmental')) return 'ecology';
    if (text.includes('international')||text.includes('global')||text.includes('abroad')||cats.includes('international')) return 'international';
    if (text.includes('technology')||text.includes('digital')||text.includes('tech')||cats.includes('technology')) return 'technology';
    if (text.includes('medical')||text.includes('health')||text.includes('medicine')||cats.includes('medical')) return 'medical';
    if (text.includes('education')||text.includes('university')||text.includes('learning')||cats.includes('education')) return 'education';
    if (text.includes('business')||text.includes('entrepreneurship')||text.includes('finance')||cats.includes('business')) return 'business';
    if (text.includes('art')||text.includes('culture')||text.includes('music')||cats.includes('arts')) return 'arts';
    if (text.includes('science')||text.includes('research')||cats.includes('science')) return 'science';
    if (text.includes('sport')||text.includes('athletic')||cats.includes('sports')) return 'sports';
    if (text.includes('agriculture')||text.includes('farming')||cats.includes('agriculture')) return 'agriculture';
    return 'local';
  };

  const getProgramSectorIds = (program) => {
    if (!program) return ['local'];
    if (Array.isArray(program.sectors) && program.sectors.length) {
      return program.sectors.map(String).filter(Boolean);
    }
    const manual = program.manual_sector || program.sector || '';
    if (manual && String(manual).trim()) {
      const parts = String(manual).split(/[,;|]/).map(s => s.trim()).filter(Boolean);
      if (parts.length) return parts;
    }
    return [inferProgramSector(program)];
  };

  const getProgramSector = (program) => getProgramSectorIds(program)[0] || 'local';

  const getProgramCategory = (program) => {
    const explicitCats = (program.categories || []);
    if (explicitCats.length > 0) {
      for (const c of explicitCats) { const found = categories.find(cat => cat.id===c||cat.label.toLowerCase()===c.toLowerCase()); if (found && found.id!=='all') return found.id; }
      const firstCat = explicitCats[0]; if (firstCat && firstCat!=='all') return firstCat;
    }
    const text = `${program.title||''} ${program.description||''} ${program.full_text||''}`.toLowerCase();
    if (text.includes('award')||text.includes('prize')||text.includes('recognition')||text.includes('medal')) return 'award';
    if (text.includes('volunteer')||text.includes('evs')||text.includes('european voluntary')) return 'volunteering';
    if (text.includes('webinar')||text.includes('mooc')||text.includes('online course')||text.includes('e-learning')) return 'online';
    if (text.includes('festival')||text.includes('competition')||text.includes('contest')||text.includes('hackathon')) return 'event';
    if (text.includes('scholarship')||text.includes('bursary')||text.includes('tuition')) return 'scholarship';
    if (text.includes('fellowship')||text.includes('fellow')) return 'fellowship';
    if (text.includes('erasmus+')||text.includes('erasmus plus')||text.includes('ka1')||text.includes('ka2')) return 'erasmusplus';
    if (text.includes('grant')||text.includes('funding')||text.includes('financial support')) return 'grant';
    if (text.includes('internship')||text.includes('placement')||text.includes('traineeship')) return 'internship';
    if (text.includes('conference')||text.includes('symposium')||text.includes('seminar')) return 'conference';
    if (text.includes('training')||text.includes('capacity building')) return 'training';
    if (text.includes('education')||text.includes('university')||text.includes('degree')||text.includes('bachelor')||text.includes('master')||text.includes('phd')) return 'education';
    for (const cat of categories) { if (cat.id==='all') continue; if (text.includes(cat.label.toLowerCase())) return cat.id; }
    return 'conference';
  };

  const getProgramSubcategory = (program) => {
    const text     = `${program.title || ''} ${program.description || ''} ${program.full_text || ''}`.toLowerCase();
    const category = getProgramCategory(program);
    if (category === 'award') {
      if (text.includes('international') || text.includes('global')) return 'international_award';
      if (text.includes('national')) return 'national_award';
      if (text.includes('regional') || text.includes('local')) return 'regional_award';
      if (text.includes('special') || text.includes('recognition')) return 'special_award';
      return 'all_award';
    }
    if (category === 'education') {
      if (text.includes('abroad') || text.includes('overseas')) return 'abroad';
      if (text.includes('bachelor') || text.includes('undergraduate')) return 'bachelor';
      if (text.includes('master') || text.includes('msc') || text.includes(' m.a')) return 'master';
      if (text.includes('phd') || text.includes('doctoral')) return 'phd';
      if (text.includes('online') || text.includes('virtual') || text.includes('e-learning')) return 'online';
      return 'all_education';
    }
    if (category === 'scholarship') {
      if (text.includes('full scholarship') || text.includes('fully funded') || text.includes('100% tuition') || text.includes('full funding')) return 'full';
      if (text.includes('partial')) return 'partial';
      if (text.includes('government') || text.includes('ministry') || text.includes('state-funded')) return 'government';
      if (text.includes('private') || text.includes('foundation') || text.includes('corporate')) return 'private';
      return 'all_scholarship';
    }
    if (category === 'fellowship') {
      if (text.includes('postdoc') || text.includes('postdoctoral')) return 'postdoc';
      if (text.includes('research')) return 'research';
      if (text.includes('visiting')) return 'visiting';
      return 'all_fellowship';
    }
    if (category === 'erasmusplus') {
      if (text.includes('ka1') || text.includes('mobility')) return 'ka1';
      if (text.includes('ka2') || text.includes('cooperation')) return 'ka2';
      if (text.includes('ka3') || text.includes('policy')) return 'ka3';
      return 'all_erasmusplus';
    }
    if (category === 'training') {
      if (text.includes('professional')) return 'professional';
      if (text.includes('technical') || text.includes('tool')) return 'technical';
      if (text.includes('soft skill')) return 'soft_skills';
      if (text.includes('online') || text.includes('virtual')) return 'online_training';
      return 'all_training';
    }
    if (category === 'grant') {
      if (text.includes('research')) return 'research_grant';
      if (text.includes('project')) return 'project_grant';
      if (text.includes('travel')) return 'travel_grant';
      return 'all_grant';
    }
    if (category === 'internship') {
      if (text.includes('summer')) return 'summer';
      if (text.includes('paid') || text.includes('stipend')) return 'paid';
      if (text.includes('unpaid')) return 'unpaid';
      if (text.includes('virtual') || text.includes('remote')) return 'virtual';
      return 'all_internship';
    }
    if (category === 'conference') {
      if (text.includes('international')) return 'international_conf';
      if (text.includes('funded') || text.includes('travel grant')) return 'funded_conf';
      if (text.includes('virtual') || text.includes('online')) return 'virtual_conf';
      return 'all_conference';
    }
    if (category === 'volunteering') {
      if (text.includes('evs') || text.includes('esc') || text.includes('european solidarity')) return 'evs';
      if (text.includes('international') || text.includes('abroad')) return 'international_vol';
      if (text.includes('local') || text.includes('community')) return 'local_vol';
      return 'all_volunteering';
    }
    if (category === 'online') {
      if (text.includes('webinar')) return 'webinar';
      if (text.includes('mooc')) return 'mooc';
      if (text.includes('course')) return 'online_course';
      return 'all_online';
    }
    if (category === 'event') {
      if (text.includes('festival')) return 'festival';
      if (text.includes('competition') || text.includes('contest')) return 'competition';
      if (text.includes('summit')) return 'summit';
      return 'all_event';
    }
    const defaults = { fellowship: 'all_fellowship', erasmusplus: 'all_erasmusplus', training: 'all_training', grant: 'all_grant', internship: 'all_internship', conference: 'all_conference' };
    return defaults[category] || 'all';
  };

  const getSectorColor   = (p) => { const s = sectors.find(x    => x.id === getProgramSector(p));   return s?.color || '#2E6B52'; };
  const getCategoryColor = (p) => { const c = categories.find(x => x.id === getProgramCategory(p)); return c?.color || '#7C8C83'; };

  const fetchApplicationForm = async (programId) => {
    if (!programId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE}/programs/${programId}/application-form`, { headers });
      const data = await res.json().catch(() => ({}));
      if (data && data.success) {
        setApplicationFormInfo(data);
        setReviewSubmitted(!!(data.myApplication && data.myApplication.orgReviewSubmitted));
        setReviewRating(0);
        setReviewComment('');
      }
    } catch {
      // best-effort; ignore failures
    }
  };

  const openProgramModal = (program) => {
    // Guests get sent straight to /registration below without ever locking body
    // scroll — locking it here first meant the redirect unmounted this component
    // before the modal's own cleanup could run, leaving the page stuck unscrollable
    // until a manual refresh.
    if (!isAuthenticated) { navigate('/registration'); return; }
    setSelectedProgram(program); setIsModalOpen(true); document.body.style.overflow = 'hidden';
    navigate(programSlugPath(program));
    trackProgramView(program.id||program._id);
    fetchApplicationForm(program.id||program._id);
  };
  const closeProgramModal = () => {
    setIsModalOpen(false); setSelectedProgram(null); document.body.style.overflow = 'auto';
    navigate('/programs');
    setApplicationFormInfo(null);
    setShowApplyFormModal(false);
  };
  const copyProgramLink = (program, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}${programSlugPath(program)}`;
    navigator.clipboard.writeText(url).then(() => showToast(t('pages.programs.linkCopiedToast'))).catch(() => {
      const el = document.createElement('textarea'); el.value = url; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); showToast(t('pages.programs.linkCopiedToast'));
    });
  };

  const handleApplyClick = () => {
    if (!isAuthenticated) { setShowRegistrationPrompt(true); return; }
    setShowApplyFormModal(true);
  };
  const handleApplicationSubmitted = (application) => {
    setApplicationFormInfo(prev => ({ ...(prev || {}), alreadyApplied: true, myApplication: application }));
    setShowApplyFormModal(false);
    showToast(t('pages.programs.applicationSubmittedToast'));
  };

  const submitOrgReview = async () => {
    const app = applicationFormInfo?.myApplication;
    if (!app || !reviewRating) return;
    setReviewSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/applications/${app.id}/organization-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating: reviewRating, comment: reviewComment }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success !== false) {
        setReviewSubmitted(true);
      } else {
        showToast(data.error || t('pages.programs.submitReviewFailedToast'));
      }
    } catch {
      showToast(t('pages.programs.submitReviewNetworkErrorToast'));
    } finally {
      setReviewSubmitting(false);
    }
  };

  // The floating "Filter" button is fixed to the viewport, so once the user
  // scrolls into the footer (nothing left to filter down there) it sits on
  // top of the footer's own links/text. Hide it for the last stretch of the
  // page instead of letting it permanently overlap the footer.
  useEffect(() => {
    const onScroll = () => {
      const scrolledToEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 220;
      setNearPageEnd(scrolledToEnd);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { const h = (e) => { if (modalRef.current && !modalRef.current.contains(e.target)) closeProgramModal(); }; if (isModalOpen) document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, [isModalOpen]);
  useEffect(() => { const h = (e) => { if (e.key==='Escape' && isModalOpen) closeProgramModal(); }; document.addEventListener('keydown', h); return () => document.removeEventListener('keydown', h); }, [isModalOpen]);
  useEffect(() => { loadPrograms(); }, [i18n.language]);
  useEffect(() => {
    const onContentLanguageChanged = () => loadPrograms();
    window.addEventListener('contentLanguageChanged', onContentLanguageChanged);
    return () => window.removeEventListener('contentLanguageChanged', onContentLanguageChanged);
  }, []);
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) loadSavedProgramIds();
    else setSavedProgramIds(new Set());
  }, [authLoading, isAuthenticated]);
  useEffect(() => {
    if (authLoading || programs.length === 0) return;
    // Deep links (shared links, or someone arriving straight from a Google
    // result for one specific opportunity) must render that opportunity's
    // content for guests too, not just logged-in users — otherwise Google
    // never sees anything but the generic listing page for any program.
    if (!routeProgramId || isModalOpen) return;
    const found = programs.find(p => String(p.id || p._id) === String(routeProgramId));
    if (found) {
      setSelectedProgram(found);
      setIsModalOpen(true);
      document.body.style.overflow = 'hidden';
      if (isAuthenticated) {
        trackProgramView(found.id||found._id);
        fetchApplicationForm(found.id||found._id);
      }
    }
  }, [authLoading, isAuthenticated, isModalOpen, programs, routeProgramId]);
  useEffect(() => { filterPrograms(); }, [programs, selectedCategory, selectedSubcategory, ageFilter, educationLevelFilter, deadlineFilter, selectedSector, sourceFilter, showArchive, greencoinsOnly]);
  // Cycles the status line on the AI matching overlay so a 6-15s wait reads as real work happening.
  useEffect(() => {
    if (!aiRecsLoading) return;
    const phrases = [
      t('pages.programs.aiPhaseReading'),
      t('pages.programs.aiPhaseScanning'),
      t('pages.programs.aiPhaseComparing'),
      t('pages.programs.aiPhaseScoring'),
      t('pages.programs.aiPhaseAlmost'),
    ];
    let i = 0;
    setAiThinkingStatus(phrases[0]);
    const id = setInterval(() => { i = (i + 1) % phrases.length; setAiThinkingStatus(phrases[i]); }, 2200);
    return () => clearInterval(id);
  }, [aiRecsLoading]);
  useEffect(() => {
    setDisplayedPrograms(filteredPrograms);
    setVisibleProgramCount(PROGRAMS_BATCH_SIZE);
  }, [filteredPrograms]);

  const filterPrograms = () => {
    let filtered = [...programs];
    if (!isAdmin) filtered = filtered.filter(p => p.approval_status==='approved'||p.status==='approved');

    filtered = filtered.filter(p => showArchive ? getProgramTimeline(p).archived : !getProgramTimeline(p).archived);

    if (selectedSector) filtered = filtered.filter(p => getProgramSectorIds(p).includes(selectedSector));
    if (selectedCategory!=='all') {
      filtered = filtered.filter(p => getProgramCategory(p)===selectedCategory);
      if (selectedSubcategory!=='all') filtered = filtered.filter(p => getProgramSubcategory(p)===selectedSubcategory);
    }
    if (sourceFilter!=='all') filtered = filtered.filter(p => getSource(p)===sourceFilter);
    if (greencoinsOnly) filtered = filtered.filter(p => p.greencoinReward > 0);
    if (ageFilter) filtered = filtered.filter(p => {
      const t = `${p.title || ''} ${p.description || ''} ${p.full_text || ''}`;
      return matchesAgeFilter(extractAgeFromText(t), ageFilter);
    });
    if (educationLevelFilter) {
      filtered = filtered.filter(p => {
        const lvl = (p.education_level || p.educationLevel || '').toString().toLowerCase().trim();
        if (lvl) return lvl === educationLevelFilter;
        const t = `${p.title || ''} ${p.description || ''} ${p.full_text || ''}`;
        return matchesEducationLevel(t, educationLevelFilter);
      });
    }

    // NEW: Deadline range filter
    if (deadlineFilter) {
      const days = parseInt(deadlineFilter, 10);
      const maxMs = days * 24 * 60 * 60 * 1000;
      filtered = filtered.filter(p => {
        const tl = getProgramTimeline(p);
        if (!tl.info || !tl.info.date) return false;
        const ms = tl.info.date.getTime() - Date.now();
        return ms > 0 && ms <= maxMs;
      });
    }

    // Programs with a greencoin reward float to the top, otherwise preserve
    // the existing order (stable sort — only the coin/no-coin split matters).
    filtered = filtered
      .map((p, i) => ({ p, i }))
      .sort((a, b) => (b.p.greencoinReward > 0) - (a.p.greencoinReward > 0) || a.i - b.i)
      .map(({ p }) => p);

    setFilteredPrograms(filtered);
  };

  // "✨ Recommended for you" — asks the backend to match this user against programs using
  // their interest profile + AI content classification, then switches the grid to show
  // only those picks (see GET /api/programs/ai-recommendations). The backend caps a real AI
  // matching run to once per user per rolling 24h to control token spend — re-clicking
  // within that window just re-serves the same cached picks for free.
  const fetchAiRecommendations = async () => {
    if (!isAuthenticated) { setShowRegistrationPrompt(true); return; }
    setAiRecsMode(true);
    setAiRecsLoading(true);
    setAiRecsError('');
    setAiThinkingPercent(0);

    // Always take a randomized 6–15s "thinking" beat before showing results, even if the
    // backend answers instantly (e.g. from the daily cache) — a real match feels considered,
    // not instant, and this also smooths over genuinely slow AI calls without a jarring jump.
    const thinkDurationMs = 6000 + Math.random() * 9000;
    const startedAt = Date.now();
    const progressTimer = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setAiThinkingPercent(Math.min(97, Math.round((elapsed / thinkDurationMs) * 97)));
    }, 120);

    try {
      const token = localStorage.getItem('token');
      const [res] = await Promise.all([
        fetch(`${API_BASE}/programs/ai-recommendations`, { headers: { Authorization: `Bearer ${token}` } }),
        new Promise((resolve) => setTimeout(resolve, thinkDurationMs)),
      ]);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || t('pages.programs.loadRecommendationsFailedError'));
      clearInterval(progressTimer);
      setAiThinkingPercent(100);
      setAiRecommendations(Array.isArray(data.recommendations) ? data.recommendations : []);
      setAiRecsCached(!!data.cached);
      setAiRecsNextAvailableAt(data.nextAvailableAt || null);
    } catch (err) {
      clearInterval(progressTimer);
      setAiRecsError(err.message || t('pages.programs.loadRecommendationsFailedError'));
      setAiRecommendations([]);
    } finally {
      setTimeout(() => setAiRecsLoading(false), 300); // let the 100% tick register before switching to results
    }
  };
  const exitAiRecsMode = () => { setAiRecsMode(false); setAiRecommendations([]); setAiRecsError(''); };

  const formatNextAvailable = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const diffMs = d.getTime() - Date.now();
    if (diffMs <= 0) return t('pages.programs.nowLabel');
    const hours = Math.floor(diffMs / (60 * 60 * 1000));
    const minutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const handleSectorClick  = (id) => setSelectedSector(prev => prev===id ? null : id);
  const toggleCategory = (categoryId) => {
    if (categoryId==='all') { setSelectedCategory('all'); setSelectedSubcategory('all'); setExpandedCategories(new Set()); setSelectedSector(null); return; }
    if (selectedCategory===categoryId) { const next=new Set(expandedCategories); next.has(categoryId)?next.delete(categoryId):next.add(categoryId); setExpandedCategories(next); }
    else { setSelectedCategory(categoryId); setSelectedSubcategory('all'); setExpandedCategories(new Set([categoryId])); }
  };
  const handleSubcategoryClick = (categoryId, subcategoryId) => { setSelectedCategory(categoryId); setSelectedSubcategory(subcategoryId); setExpandedCategories(new Set([categoryId])); };
  const clearAllFilters = () => { setSelectedCategory('all'); setSelectedSubcategory('all'); setSelectedSector(null); setAgeFilter(''); setEducationLevelFilter(''); setDeadlineFilter(''); setSourceFilter('all'); setShowArchive(false); setGreencoinsOnly(false); };

  const isValidImageUrl = (url) => url && url!=='null' && url!=='undefined' && url.trim()!=='';

  const pendingCount = programs.filter(p => {
    const st = p.approval_status || p.status || 'pending';
    return st === 'pending';
  }).length;

  const hasActiveFilters = selectedSector || selectedCategory !== 'all' || selectedSubcategory !== 'all' || ageFilter || educationLevelFilter || deadlineFilter || sourceFilter !== 'all' || showArchive || greencoinsOnly;

  const aiReasonByProgramId = new Map(aiRecommendations.map(r => [String(r.program?.id || r.program?._id), r.reason]));
  const gridPrograms = aiRecsMode
    ? aiRecommendations.map(r => r.program).filter(p => p && !getProgramTimeline(p).archived)
    : displayedPrograms.slice(0, isAuthenticated ? visibleProgramCount : previewLimit);

  // ── Deadline badge renderer — application deadline, labeled "Դիմելու վերջնաժամկետ" ──
  const renderDeadlineBadge = (deadlineInfo, compact = false) => {
    if (!deadlineInfo) return null;
    const soon = isDeadlineSoon(deadlineInfo);
    const expired = deadlineInfo.expired;
    const label = t('pages.programs.applyDeadlineLabel');

    let cls, icon, value;
    if (expired) {
      cls = 'pp-date-badge-deadline-expired'; icon = 'fa-solid fa-hourglass-end';
      value = t('pages.programs.deadlinePassed');
    } else if (soon) {
      cls = 'pp-date-badge-deadline-soon'; icon = 'fa-solid fa-bolt';
      value = deadlineInfo.formatted;
    } else {
      cls = 'pp-date-badge-deadline-open'; icon = 'fa-solid fa-hourglass-half';
      value = deadlineInfo.formatted;
    }

    return (
      <div className={`pp-date-badge ${cls}`}>
        <span className="pp-date-badge-icon"><i className={icon}></i></span>
        <span className="pp-date-badge-text">
          <span className="pp-date-badge-label">{label}</span>
          <span className="pp-date-badge-value">{value}</span>
        </span>
      </div>
    );
  };

  // ── Event date badge renderer — when the program/event takes place (срок проведения) ──
  // Shown on every card as a small, non-urgency-colored label, separate from the
  // application-deadline badge above.
  const renderEventDateBadge = (eventInfo) => {
    if (!eventInfo) return null;
    const expired = eventInfo.expired;
    return (
      <div className={`pp-date-badge ${expired ? 'pp-date-badge-event-expired' : 'pp-date-badge-event'}`}>
        <span className="pp-date-badge-icon"><i className="fa-solid fa-calendar-days"></i></span>
        <span className="pp-date-badge-text">
          <span className="pp-date-badge-label">{t('pages.programs.eventDateLabel')}</span>
          <span className="pp-date-badge-value">{expired ? t('pages.programs.eventDatePassed') : eventInfo.formatted}</span>
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'linear-gradient(180deg, #16281f 0%, #1a3a2c 38%, #16281f 100%)', fontFamily:"'Plus Jakarta Sans',sans-serif", gap:16 }}>
        <div style={{ width:44, height:44, aspectRatio:'1 / 1', boxSizing:'border-box', flex:'0 0 auto', border:'3px solid rgba(255,255,255,0.14)', borderTopColor:'#ffd873', borderRadius:'50%', animation:'pp-spin 0.8s linear infinite' }}></div>
        <p style={{ color:'rgba(234,243,238,0.65)', fontSize:14, fontWeight:500 }}>{t('pages.programs.loadingPrograms')}</p>
        <style>{`@keyframes pp-spin{to{transform:rotate(360deg);}}`}</style>
      </div>
    );
  }

  return (
    <>
      <SEO
        titleKey="pages.seo.programs.title"
        descriptionKey="pages.seo.programs.description"
        keywordsKey="pages.seo.programs.keywords"
        path="/programs"
        breadcrumbLabel="Programs"
        jsonLd={programs.length > 0 ? [{
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: programs.slice(0, 50).map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "EducationalOccupationalProgram",
              name: p.title,
              description: (p.description || '').slice(0, 300),
              url: `https://greenwich.am${programSlugPath(p)}`,
              image: p.image_url || undefined,
              provider: p.organizationName ? { "@type": "Organization", name: p.organizationName } : undefined,
            },
          })),
        }] : undefined}
      />
      {/* When a specific opportunity is open (deep link, shared link, or a click),
          override the generic /programs tags with this program's own title,
          description, image and structured data — rendered after the generic
          <SEO> above, so react-helmet-async's last-wins merge picks these up.
          This is what lets a search like "Erasmus internship Armenia" surface
          this exact opportunity instead of just the generic programs page. */}
      {selectedProgram && (
        <SEO
          titleKey="pages.seo.programs.title"
          descriptionKey="pages.seo.programs.description"
          path={programSlugPath(selectedProgram)}
          breadcrumbLabel={selectedProgram.title}
          image={selectedProgram.image_url || undefined}
          overrideTitle={`${selectedProgram.title}${selectedProgram.organizationName ? ` — ${selectedProgram.organizationName}` : ''} | Greenwich`}
          overrideDescription={(selectedProgram.description || '').slice(0, 300) || undefined}
          jsonLd={[{
            "@context": "https://schema.org",
            "@type": "EducationalOccupationalProgram",
            name: selectedProgram.title,
            description: (selectedProgram.description || '').slice(0, 500),
            url: `https://greenwich.am${programSlugPath(selectedProgram)}`,
            image: selectedProgram.image_url || undefined,
            provider: selectedProgram.organizationName ? { "@type": "Organization", name: selectedProgram.organizationName } : undefined,
            applicationDeadline: selectedProgram.deadline || undefined,
            locationCreated: selectedProgram.location || undefined,
          }]}
        />
      )}
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"/>

      <div className="pp-root">
        {toast && <div className="pp-toast">{toast}</div>}
        <Registratioon isOpen={showRegistrationPrompt} onClose={() => setShowRegistrationPrompt(false)} />

        {deleteConfirm && (
          <div className="pp-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="pp-confirm" onClick={e => e.stopPropagation()}>
              <div style={{ fontSize:36, marginBottom:12 }}>🗑</div>
              <h3>{t('pages.programs.deleteConfirmTitle')}</h3>
              <p>{t('pages.programs.deleteConfirmBody')}</p>
              <div className="pp-confirm-actions">
                <button className="pp-btn-ghost clay-press clay-focus-glow" onClick={() => setDeleteConfirm(null)}>{t('pages.programs.cancel')}</button>
                <button className="pp-btn-danger clay-press clay-focus-glow" onClick={() => doDelete(deleteConfirm)}>
                  {actionLoading[deleteConfirm]==='delete' ? <span className="pp-spin-sm"/> : t('pages.programs.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin bar */}
        {isAdmin && (
          <div className="pp-admin-bar">
            <span className="pp-admin-label"><AdminIcon/> Admin Mode</span>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {pendingCount > 0 && (
                <button className="pp-pending-btn clay-press clay-focus-glow" onClick={() => navigate('/admin/programs-review')}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  {pendingCount > 99 ? '99+' : pendingCount} pending
                </button>
              )}
              <button className="pp-manage-btn clay-press clay-focus-glow" onClick={() => navigate('/admin/programs-review')}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                Manage
              </button>
            </div>
          </div>
        )}

        {/* Org bar */}
        {isOrg && (
          <div className="pp-org-bar">
            <span>{t('pages.programs.orgBarQuestion')}</span>
            <div style={{ display:'flex', gap:8 }}>
              <button className="pp-org-btn clay-press clay-focus-glow" onClick={() => navigate('/programs/my-submissions')}><OrgIcon/> {t('pages.programs.mySubmissions')}</button>
              <button className="pp-org-btn clay-press clay-focus-glow" onClick={() => navigate('/programs/submit')}><OrgIcon/> {t('pages.programs.submitNew')}</button>
            </div>
          </div>
        )}

        {/* Hero — the Source/Sector pill rows, the age/education/deadline selects and the
            Archive toggle used to be spread across three separate stacked blocks here
            (a hero pill row, a second sector pill row, and a third filter bar further
            down). They now all live together inside the single pp-sidebar filter panel
            below, so every one of those filter dimensions still works exactly as before —
            only the presentation is unified into one drawer/panel instead of three. */}
        <div className="pp-hero">
          <div className="pp-hero-headrow">
            <div className="pp-hero-titlewrap">
              <h1 className="pp-hero-title">{t('pages.programs.heroTitle')}</h1>
              <p className="pp-hero-sub">{t('pages.programs.heroSubtitle')}</p>
            </div>
            {/* Greencoin rewards is a rewards-shop-style shortcut, not a category — it used
                to be parked as the first item inside the sidebar's "Categories" list, which
                made it read like a category value when it's actually an independent
                greencoinReward>0 filter. Moved up near the header instead, still wired to
                the same greencoinsOnly state/filter. */}
            <button
              type="button"
              className={`pp-greencoin-shortcut${greencoinsOnly ? ' pp-greencoin-shortcut-active' : ''}`}
              onClick={() => setGreencoinsOnly(v => !v)}
              title={t('pages.programs.greencoinsFilter')}
            >
              <span className="pp-greencoin-shortcut-icon">🪙</span>
              <span className="pp-greencoin-shortcut-label">{t('pages.programs.greencoinsFilter')}</span>
              <span className="pp-greencoin-shortcut-count">{programs.filter(p => p.greencoinReward > 0).length}</span>
            </button>
          </div>
        </div>

        <div className="pp-layout">
          <button
            className={`pp-sidebar-toggle clay-press clay-focus-glow${nearPageEnd && !sidebarOpen ? ' pp-sidebar-toggle-hidden' : ''}`}
            onClick={() => setSidebarOpen(v => !v)}
          >
            <i className={`fas fa-${sidebarOpen?'times':'filter'}`}></i>
            {sidebarOpen ? t('pages.programs.sidebarClose') : t('pages.programs.sidebarFilter')}
          </button>
          {sidebarOpen && <div className="pp-sidebar-backdrop" onClick={() => setSidebarOpen(false)}/>}

          {/* Sidebar — the single unified filter panel. On desktop it's a persistent
              column; on narrow screens it collapses to the same off-canvas drawer it
              always did (toggled via pp-sidebar-toggle above). Every filter dimension
              that used to be scattered across the hero pill rows and the old filter bar
              now lives here as its own labeled section, sharing one visual pattern. */}
          <aside className={`pp-sidebar${sidebarOpen?' pp-sidebar-open':''}`}>
            <div className="pp-sidebar-header">
              <h3><i className="fas fa-sliders-h" aria-hidden="true"></i> {t('pages.programs.filtersLabel')}</h3>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {hasActiveFilters && <button className="pp-sidebar-clear" onClick={clearAllFilters}>{t('pages.programs.clear')}</button>}
                <button className="pp-sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
              </div>
            </div>

            {/* Source */}
            <div className="pp-filter-section">
              <div className="pp-source-filters">
                <span className="pp-source-label">{t('pages.programs.sourceLabel')}</span>
                {Object.entries(SOURCE_META).map(([k, v]) => (
                  <button key={k} className={`pp-source-btn ${v.cls}${sourceFilter===k?' pp-active':''}`} onClick={() => setSourceFilter(sourceFilter===k?'all':k)}>
                    <v.Icon/> {sourceLabels[k]}
                  </button>
                ))}
                {sourceFilter!=='all' && <button className="pp-source-clear" onClick={() => setSourceFilter('all')}>✕</button>}
              </div>
            </div>

            {/* Sectors — broad topic tags, a different taxonomy from the program-type
                categories below (e.g. a "technology" program can be a scholarship,
                fellowship or internship) — both are real, independent filters. */}
            <div className="pp-filter-section">
              <div className="pp-sectors-grid">
                {sectors.map(sector => (
                  <button key={sector.id} className={`pp-sector${selectedSector===sector.id?' pp-sector-active':''}`} onClick={() => handleSectorClick(sector.id)} style={{ '--sc':sector.color }}>
                    <div className="pp-sector-icon"><i className={sector.icon}></i></div>
                    <span>{sector.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Program type / categories */}
            <div className="pp-filter-section pp-filter-section--cats">
              <div className="pp-cat-list">
                {categories.map(category => (
                  <div key={category.id} className="pp-cat-group">
                    <button className={`pp-cat-item${selectedCategory===category.id?' pp-cat-active':''}`} onClick={() => { toggleCategory(category.id); setSidebarOpen(false); }} style={{ '--cc':category.color }}>
                      <div className="pp-cat-icon-box" style={{ background:selectedCategory===category.id?category.color:`${category.color}20`, color:selectedCategory===category.id?'white':category.color }}>
                        <i className={category.icon}></i>
                      </div>
                      <span className="pp-cat-name">{category.label}</span>
                      {category.subcategories?.length > 0 && <i className={`fas fa-chevron-${expandedCategories.has(category.id)?'up':'down'} pp-cat-arrow`}></i>}
                    </button>
                    {category.subcategories?.length > 0 && expandedCategories.has(category.id) && (
                      <div className="pp-subcat-list">
                        {category.subcategories.map(sub => (
                          <button key={sub.id} className={`pp-subcat-item${selectedCategory===category.id&&selectedSubcategory===sub.id?' pp-subcat-active':''}`} onClick={() => { handleSubcategoryClick(category.id, sub.id); setSidebarOpen(false); }} style={{ '--cc':category.color }}>
                            <i className={sub.icon} style={{ color:category.color, fontSize:11 }}></i>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Age / education level / deadline — used to be a separate filter bar
                above the results grid; same three selects, same state, now grouped
                with every other filter instead of forming their own stacked block. */}
            <div className="pp-filter-section pp-filter-section--selects">
              <div className="pp-filter-group pp-filter-group--age">
                <label><i className="fas fa-user-clock" aria-hidden="true"></i><span className="pp-filter-group-text">{t('pages.programs.ageLabel')}</span></label>
                <select value={ageFilter} onChange={e => setAgeFilter(e.target.value)} className="pp-select">
                  {ageOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="pp-filter-group pp-filter-group--edu">
                <label><i className="fas fa-graduation-cap" aria-hidden="true"></i><span className="pp-filter-group-text">{t('pages.programs.educationLevelLabel')}</span></label>
                <select value={educationLevelFilter} onChange={e => setEducationLevelFilter(e.target.value)} className="pp-select">
                  {educationLevelOptions.map(o => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="pp-filter-group pp-filter-group--deadline">
                <label><i className="fas fa-calendar-alt" aria-hidden="true"></i><span className="pp-filter-group-text">{t('pages.programs.deadlineLabel')}</span></label>
                <select value={deadlineFilter} onChange={e => setDeadlineFilter(e.target.value)} className="pp-select">
                  {deadlineOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {/* Archive toggle — also a real filter (flips the active/expired set),
                kept in the same panel as everything else instead of its own row. */}
            <button
              type="button"
              className={`pp-archive-btn${showArchive ? ' active' : ''}`}
              onClick={() => setShowArchive(v => !v)}
              title={showArchive ? t('pages.programs.archiveShowActive') : t('pages.programs.archiveShowArchive')}>
              <i className="fas fa-archive"></i> {t('pages.programs.archiveLabel')}
            </button>
          </aside>

          {/* Main */}
          <main className="pp-main">
            {/* AI Matching stays a distinct top-level action rather than a filter —
                unlike everything in the sidebar, it doesn't narrow the current list,
                it replaces it with a separately-fetched recommendation set. */}
            <div className="pp-toolbar">
              <button
                type="button"
                className={`pp-archive-btn pp-archive-btn--ai${aiRecsMode ? ' active' : ''}`}
                onClick={fetchAiRecommendations}
                disabled={aiRecsLoading}
                title={aiRecsCached && aiRecsNextAvailableAt ? t('pages.programs.aiNewMatchesIn', { time: formatNextAvailable(aiRecsNextAvailableAt) }) : t('pages.programs.aiShowProgramsMatchedTooltip')}>
                <span className="pp-ai-tag">{t('pages.programs.aiMatchingTag')}</span> {aiRecsLoading ? t('pages.programs.aiThinkingButton') : aiRecsMode && aiRecsCached ? t('pages.programs.aiMatchedToday') : t('pages.programs.recommendedTitle')}
              </button>
              {aiRecsMode && !aiRecsLoading && (
                <>
                  {aiRecsCached && aiRecsNextAvailableAt && (
                    <span className="pp-results-count" style={{ alignSelf: 'center' }}>{t('pages.programs.aiNextMatchIn', { time: formatNextAvailable(aiRecsNextAvailableAt) })}</span>
                  )}
                  <button className="pp-clear-btn clay-press clay-focus-glow" onClick={exitAiRecsMode}><i className="fas fa-times"></i> {t('pages.programs.aiShowAllPrograms')}</button>
                </>
              )}
              {hasActiveFilters && <button className="pp-clear-btn pp-clear-btn--strong clay-press clay-focus-glow" onClick={clearAllFilters}><i className="fas fa-times"></i> {t('pages.programs.clear')}</button>}
            </div>

            {hasActiveFilters && (
              <div className="pp-active-filters">
                {sourceFilter!=='all' && <span className={`pp-tag source-${sourceFilter}`}>{sourceLabels[sourceFilter]}<button onClick={() => setSourceFilter('all')}>×</button></span>}
                {selectedSector && <span className="pp-tag" style={{ '--tc':sectors.find(s=>s.id===selectedSector)?.color }}>{sectors.find(s=>s.id===selectedSector)?.label}<button onClick={() => setSelectedSector(null)}>×</button></span>}
                {selectedCategory!=='all' && <span className="pp-tag" style={{ '--tc':categories.find(c=>c.id===selectedCategory)?.color }}>{categories.find(c=>c.id===selectedCategory)?.label}<button onClick={() => { setSelectedCategory('all'); setSelectedSubcategory('all'); }}>×</button></span>}
                {selectedSubcategory!=='all' && <span className="pp-tag pp-tag-sub">{categories.find(c=>c.id===selectedCategory)?.subcategories?.find(s=>s.id===selectedSubcategory)?.label}<button onClick={() => setSelectedSubcategory('all')}>×</button></span>}
                {ageFilter    && <span className="pp-tag">{t('pages.programs.ageLabel')}: {ageOptions.find(a=>a.value===ageFilter)?.label}<button onClick={() => setAgeFilter('')}>×</button></span>}
                {educationLevelFilter && <span className="pp-tag">{t('pages.programs.educationLevelShort')}: {educationLevelOptions.find(o => o.value === educationLevelFilter)?.label}<button type="button" onClick={() => setEducationLevelFilter('')}>×</button></span>}
                {deadlineFilter && <span className="pp-tag" style={{ '--tc':'#d97706' }}>⏳ {deadlineOptions.find(o => o.value === deadlineFilter)?.label}<button onClick={() => setDeadlineFilter('')}>×</button></span>}
                {showArchive && <span className="pp-tag" style={{ '--tc':'#6b7280' }}>{t('pages.programs.archiveLabel')}<button onClick={() => setShowArchive(false)}>×</button></span>}
                {greencoinsOnly && <span className="pp-tag" style={{ '--tc':'#d69b1f' }}>🪙 {t('pages.programs.greencoinsFilter')}<button onClick={() => setGreencoinsOnly(false)}>×</button></span>}
              </div>
            )}

            <div className="pp-results-header">
              <div>
                <h2 className="pp-results-title">
                  {aiRecsMode ? <>✨ {t('pages.programs.recommendedTitle')}</> : selectedSector ? sectors.find(s=>s.id===selectedSector)?.label : selectedCategory!=='all' ? categories.find(c=>c.id===selectedCategory)?.label : t('pages.programs.allPrograms')}
                  {selectedSubcategory!=='all' && selectedCategory!=='all' && <span className="pp-sub-label"> / {categories.find(c=>c.id===selectedCategory)?.subcategories?.find(s=>s.id===selectedSubcategory)?.label}</span>}
                  {showArchive && <span className="pp-sub-label"> / {t('pages.programs.archiveLabel')}</span>}
                </h2>
                <p className="pp-results-count">{aiRecsMode ? t('pages.programs.programsMatchedCount', { count: gridPrograms.length }) : t('pages.programs.programsFound', { count: filteredPrograms.length })}</p>
                {!isAuthenticated && filteredPrograms.length > previewLimit && (
                  <p className="pp-results-count" style={{ marginTop: 8, color: '#ffd873', fontWeight: 700 }}>
                    {t('pages.programs.unlockPreviewText', { previewLimit, count: filteredPrograms.length })}{' '}
                    <button
                      type="button"
                      onClick={() => setShowRegistrationPrompt(true)}
                      style={{ border: 'none', background: 'none', padding: 0, color: '#ffd873', fontWeight: 800, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
                    >
                      {t('pages.programs.unlockFullListCta')}
                    </button>
                  </p>
                )}
              </div>
            </div>

            {aiRecsMode && aiRecsError ? (
              <div className="pp-empty">
                <div className="pp-empty-icon">⚠️</div>
                <h3>{t('pages.programs.aiCouldNotLoad')}</h3>
                <p>{aiRecsError}</p>
                <button className="pp-reset-btn clay-press clay-focus-glow" onClick={fetchAiRecommendations}>{t('pages.programs.tryAgain')}</button>
              </div>
            ) : (aiRecsMode ? (gridPrograms.length === 0 && !aiRecsLoading) : filteredPrograms.length === 0) ? (
              <div className="pp-empty">
                <div className="pp-empty-icon">📭</div>
                <h3>{aiRecsMode ? t('pages.programs.aiNoStrongMatches') : showArchive ? t('pages.programs.archiveEmpty') : t('pages.programs.noPrograms')}</h3>
                <p>{aiRecsMode ? t('pages.programs.aiBrowseAndSave') : t('pages.programs.adjustFilters')}</p>
                <button className="pp-reset-btn clay-press clay-focus-glow" onClick={aiRecsMode ? exitAiRecsMode : clearAllFilters}>{aiRecsMode ? t('pages.programs.aiShowAllPrograms') : t('pages.programs.resetFilters')}</button>
              </div>
            ) : (
              <div className="pp-grid">
                {gridPrograms.map((program) => {
                  const sectorColor   = getSectorColor(program);
                  const categoryColor = getCategoryColor(program);
                  const sectorObj     = sectors.find(s    => s.id===getProgramSector(program));
                  const categoryObj   = categories.find(c => c.id===getProgramCategory(program));
                  const hasImage      = isValidImageUrl(program.image_url);
                  const src           = getSource(program);
                  const srcMeta       = SOURCE_META[src] || SOURCE_META.ai;
                  const isPending     = program.approval_status==='pending'  || program.status==='pending';
                  const isRejected    = program.approval_status==='rejected' || program.status==='rejected';
                  const timeline      = getProgramTimeline(program);
                  const deadlineInfo  = timeline.info;
                  const eventDateInfo = timeline.eventInfo;
                  const isArchivedCard = timeline.archived;
                  const { fields: descFields, rest: descRest } = extractFieldLines(program.description);
                  const descBullets = splitIntoBullets(descRest);

                  return (
                    <div key={program.id||program._id}
                      className={`pp-card clay-lift${isPending&&isAdmin?' pp-card-pending':''}${isRejected&&isAdmin?' pp-card-rejected':''}${isArchivedCard?' pp-card-archived':''}${program.greencoinReward>0?' pp-card-greencoin':''}`}
                      style={{ '--accent': sectorColor }}
                      onClick={() => {
                        if (editingId) return;
                        openProgramModal(program);
                      }}>

                      {isAdmin && isPending  && <div className="pp-ribbon pp-ribbon-pending">{t('pages.programs.pending')}</div>}
                      {isAdmin && isRejected && <div className="pp-ribbon pp-ribbon-rejected">{t('pages.programs.rejected')}</div>}

                      <div className="pp-card-image">
                        {hasImage ? (
                          <img src={program.image_url} alt={program.title} loading="lazy" decoding="async"
                            onError={e => { e.target.style.display='none'; e.target.parentElement.style.background=`linear-gradient(135deg,${sectorColor}18,${sectorColor}35)`; e.target.parentElement.innerHTML=`<div class="pp-card-img-fallback"><div class="pp-card-img-icon" style="background:${sectorColor}25;color:${sectorColor}"><i class="${sectorObj?.icon||'fas fa-globe'}"></i></div></div>`; }}
                          />
                        ) : (
                          <div className="pp-card-img-fallback" style={{ background:`linear-gradient(135deg,${sectorColor}18,${sectorColor}35)` }}>
                            <div className="pp-card-img-icon" style={{ background:`${sectorColor}25`, color:sectorColor }}>
                              <i className={sectorObj?.icon||'fas fa-globe'}></i>
                            </div>
                          </div>
                        )}
                        <span className={`pp-source-pill ${srcMeta.cls}`} onClick={e => e.stopPropagation()}>
                          <srcMeta.Icon/> {getSourceLabel(program, t)}
                        </span>
                      </div>

                      {isAdmin && editingId===(program.id||program._id) ? (
                        <div className="pp-inline-edit" onClick={e => e.stopPropagation()}>
                          <input className="pp-edit-input" value={editForm.title||''} onChange={e => setEditForm(p=>({...p,title:e.target.value}))} placeholder={t('pages.programs.title')}/>
                          <textarea className="pp-edit-textarea" rows={3} value={editForm.description||''} onChange={e => setEditForm(p=>({...p,description:e.target.value}))} placeholder={t('pages.programs.description')}/>
                          <label style={{ fontSize:11, fontWeight:700, color:'rgba(234,243,238,0.65)' }}>{t('pages.programs.educationLevelLabel')}</label>
                          <select className="pp-select" style={{ width:'100%' }} value={editForm.education_level || ''} onChange={e => setEditForm(p => ({ ...p, education_level: e.target.value }))}>
                            {educationLevelOptions.map(o => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
                          </select>
                          <div className="pp-edit-actions">
                            <button className="pp-btn-ghost clay-press clay-focus-glow" onClick={() => setEditingId(null)}>{t('pages.programs.cancel')}</button>
                            <button className="pp-btn-save clay-press clay-focus-glow" onClick={() => saveEdit(program.id||program._id)}>
                              {actionLoading[program.id||program._id]==='edit'?<span className="pp-spin-sm"/>:t('pages.programs.save')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="pp-card-body">
                          <div className="pp-card-meta" style={{ flexWrap:'wrap', gap:6 }}>
                            {getProgramSectorIds(program).map((sid, idx) => {
                              const so = sectors.find(s => s.id === sid);
                              const col = so?.color || sectorColor;
                              return (
                                <span key={`${sid}-${idx}`} className="pp-card-sector" style={{ color:col, background:`${col}12` }}>
                                  <i className={so?.icon||'fas fa-globe'}></i> {so?.label || sid}
                                </span>
                              );
                            })}
                            <span className="pp-card-type" style={{ color:categoryColor }}>{categoryObj?.label||getProgramCategory(program)}</span>
                          </div>
                          <h3 className="pp-card-title">{program.title}</h3>
                          {program.greencoinReward > 0 && (
                            <div className="pp-greencoin-badge">
                              🪙 {t('pages.programs.greencoinReward', { amount: program.greencoinReward })}
                            </div>
                          )}
                          {aiRecsMode && aiReasonByProgramId.get(String(program.id||program._id)) && (
                            <div className="pp-tag" style={{ '--tc':'#7c3aed', marginBottom: 6 }}>
                              ✨ {aiReasonByProgramId.get(String(program.id||program._id))}
                            </div>
                          )}
                          {descFields.length > 0 && (
                            <div className="pp-card-fields">
                              {descFields.map((f, i) => (
                                <div key={i} className="pp-card-field-row">
                                  <span className="pp-card-field-icon" style={{ background:`${f.color}20`, color:f.color }}><i className={f.icon}></i></span>
                                  <span className="pp-card-field-text">{f.text}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {descBullets.length > 0 && (
                            <ul className="pp-card-desc-list">
                              {descBullets.map((b, i) => <li key={i}>{b}</li>)}
                            </ul>
                          )}
                          <div className="pp-date-badges">
                            {/* Application deadline badge, labeled "Դիմելու վերջնաժամկետ" */}
                            {renderDeadlineBadge(deadlineInfo)}
                            {/* Event date badge — when the program/event actually takes place */}
                            {renderEventDateBadge(eventDateInfo)}
                          </div>
                          <div className="pp-card-footer" style={{ justifyContent: isAuthenticated ? 'space-between' : 'flex-end' }}>
                            {isAuthenticated && (
                              <button
                                type="button"
                                className={`pp-save-btn${savedProgramIds.has(String(program.id||program._id)) ? ' pp-save-active' : ''}`}
                                onClick={(e) => toggleSaveProgram(e, program)}
                                title={savedProgramIds.has(String(program.id||program._id)) ? t('pages.programs.unsaveProgram') : t('pages.programs.saveProgram')}
                                aria-label={savedProgramIds.has(String(program.id||program._id)) ? t('pages.programs.unsaveProgram') : t('pages.programs.saveProgram')}
                                style={{ '--sc': sectorColor }}
                              >
                                <i className={savedProgramIds.has(String(program.id||program._id)) ? 'fas fa-bookmark' : 'far fa-bookmark'}></i>
                              </button>
                            )}
                            <span className="pp-card-link" style={{ color:sectorColor }}>
                              {t('pages.programs.viewDetails')}
                              <span className="pp-link-arrow" style={{ background:sectorColor }}><i className="fa-solid fa-arrow-right"></i></span>
                            </span>
                          </div>
                          {isAdmin && (
                            <div className="pp-card-admin" onClick={e => e.stopPropagation()}>
                              {isPending && (
                                <>
                                  <button className="pp-action-approve clay-press" disabled={!!actionLoading[program.id||program._id]} onClick={() => doStatusChange(program.id||program._id,'approve')}>
                                    {actionLoading[program.id||program._id]==='approve'?<span className="pp-spin-sm"/>:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                                    {t('pages.programs.approve')}
                                  </button>
                                  <button className="pp-action-reject clay-press" disabled={!!actionLoading[program.id||program._id]} onClick={() => doStatusChange(program.id||program._id,'reject')}>
                                    {actionLoading[program.id||program._id]==='reject'?<span className="pp-spin-sm"/>:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
                                    {t('pages.programs.reject')}
                                  </button>
                                </>
                              )}
                              {!isPending && (
                                <>
                                  <button className="pp-action-toggle clay-press" disabled={!!actionLoading[program.id||program._id]} onClick={() => doStatusChange(program.id||program._id,isRejected?'approve':'reject')}>
                                    {actionLoading[program.id||program._id]?<span className="pp-spin-sm"/>:isRejected?(<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>{t('pages.programs.reapprove')}</>):(<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>{t('pages.programs.revoke')}</>)}
                                  </button>
                                  <button className="pp-icon-btn pp-icon-edit clay-press" onClick={() => { setEditingId(program.id||program._id); setEditForm({ title:program.title, description:program.description, education_level: program.education_level || program.educationLevel || '' }); }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    <span>{t('pages.programs.edit')}</span>
                                  </button>
                                  <button className="pp-icon-btn pp-icon-danger clay-press" onClick={() => setDeleteConfirm(program.id||program._id)}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                    <span>{t('pages.programs.delete')}</span>
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>
            )}
            {!isAuthenticated && filteredPrograms.length > previewLimit && (
              <div
                onClick={() => setShowRegistrationPrompt(true)}
                style={{
                  marginTop: 24,
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 28,
                  minHeight: 260,
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(50,97,77,0.08), rgba(15,23,42,0.06))',
                  border: '1px solid rgba(50, 97, 77, 0.14)',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.82) 100%)',
                    backdropFilter: 'blur(10px)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: 24,
                  }}
                >
                  <div style={{ fontSize: 42, marginBottom: 12 }}>🔒</div>
                  <h3 style={{ margin: '0 0 10px', fontSize: 24, color: '#1f2937', fontWeight: 800 }}>
                    {t('pages.programs.unlockMoreWaiting', { count: Math.max(filteredPrograms.length - previewLimit, 0) })}
                  </h3>
                  <p style={{ margin: '0 0 16px', maxWidth: 480, color: '#475569', fontSize: 15, lineHeight: 1.6 }}>
                    {t('pages.programs.unlockFullListText')}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowRegistrationPrompt(true);
                    }}
                    style={{
                      border: 'none',
                      borderRadius: 999,
                      padding: '12px 20px',
                      background: 'linear-gradient(135deg, #32614d, #2a5240)',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {t('pages.programs.unlockFullListCta')}
                  </button>
                </div>
              </div>
            )}
            {!aiRecsMode && isAuthenticated && visibleProgramCount < displayedPrograms.length && (
              <div className="pp-see-more-wrap">
                <button
                  type="button"
                  className="pp-see-more-btn clay-press clay-focus-glow"
                  onClick={() => setVisibleProgramCount(count => Math.min(count + PROGRAMS_BATCH_SIZE, displayedPrograms.length))}
                >
                  {t('pages.home.seeMore')}
                </button>
              </div>
            )}
          </main>
        </div>

        {/* AI matching overlay — full-screen blur + progress while Kimi analyzes matches */}
        {aiRecsLoading && (
          <div className="pp-ai-overlay">
            <div className="pp-ai-overlay-card">
              <div className="pp-ai-overlay-icon">
                <i className="fas fa-wand-magic-sparkles"></i>
              </div>
              <div className="pp-ai-ring">
                <svg viewBox="0 0 100 100">
                  <circle className="pp-ai-ring-track" cx="50" cy="50" r="44" />
                  <circle
                    className="pp-ai-ring-fill"
                    cx="50" cy="50" r="44"
                    style={{ strokeDashoffset: 276.5 - (276.5 * aiThinkingPercent) / 100 }}
                  />
                </svg>
                <span className="pp-ai-ring-pct">{aiThinkingPercent}%</span>
              </div>
              <h3 className="pp-ai-overlay-title">{aiThinkingStatus}</h3>
              <p className="pp-ai-overlay-sub">{t('pages.programs.aiOverlaySub')}</p>
            </div>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && selectedProgram && (() => {
          const tl = getProgramTimeline(selectedProgram);
          return (
            <div className="pp-modal-overlay clay-modal-backdrop">
              <div className="pp-modal clay-modal-enter" ref={modalRef}>
                <button className="pp-modal-close clay-press clay-focus-glow" onClick={closeProgramModal}><i className="fas fa-times"></i></button>
                <div className="pp-modal-inner">
                  <div className="pp-modal-media" style={{ background:`linear-gradient(135deg,${getSectorColor(selectedProgram)}15,${getSectorColor(selectedProgram)}30)` }}>
                    {isValidImageUrl(selectedProgram.image_url) ? (
                      <img src={selectedProgram.image_url} alt={selectedProgram.title} className="pp-modal-img"
                        onError={e => { const sc=getSectorColor(selectedProgram); e.target.style.display='none'; e.target.parentElement.innerHTML=`<div class="pp-modal-img-fallback"><div style="width:80px;height:80px;border-radius:50%;background:${sc}30;display:flex;align-items:center;justify-content:center;"><i class="${sectors.find(s=>s.id===getProgramSector(selectedProgram))?.icon||'fas fa-globe'}" style="font-size:34px;color:${sc}"></i></div></div>`; }}
                      />
                    ) : (
                      <div className="pp-modal-img-fallback">
                        <div style={{ width:80, height:80, borderRadius:'50%', background:`${getSectorColor(selectedProgram)}30`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <i className={sectors.find(s=>s.id===getProgramSector(selectedProgram))?.icon||'fas fa-globe'} style={{ fontSize:34, color:getSectorColor(selectedProgram) }}></i>
                        </div>
                      </div>
                    )}
                    <div className="pp-modal-tags-left" style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                      {getProgramSectorIds(selectedProgram).map((sid, idx) => {
                        const so = sectors.find(s => s.id === sid);
                        const sc = so?.color || getSectorColor(selectedProgram);
                        return (
                          <span key={`${sid}-${idx}`} className="pp-modal-sector-tag" style={{ background:sc }}>
                            <i className={so?.icon||'fas fa-globe'}></i>
                            {so?.label || sid}
                          </span>
                        );
                      })}
                      <span className="pp-modal-cat-tag" style={{ background:getCategoryColor(selectedProgram) }}>
                        {categories.find(c=>c.id===getProgramCategory(selectedProgram))?.label}
                      </span>
                      {(() => { const s=getSource(selectedProgram); const m=SOURCE_META[s]; return <span className={`pp-modal-source-tag ${m.cls}`}><m.Icon/> {getSourceLabel(selectedProgram, t)}</span>; })()}
                    </div>
                  </div>

                  <div className="pp-modal-details">
                    <h2 className="pp-modal-title">{selectedProgram.title}</h2>
                    {(tl.info || selectedProgram.funding_type || selectedProgram.location || selectedProgram.duration || selectedProgram.end_date || selectedProgram.target_group || selectedProgram.format || selectedProgram.source) && (
                      <div className="pp-modal-info-grid">
                        {tl.info && (
                          <div className="pp-modal-info-item" style={isDeadlineSoon(tl.info) ? { background:'#fef2f2', border:'1px solid #fca5a5' } : {}}>
                            <span className="pp-modal-info-icon" style={{ background:'#dc262620', color:'#dc2626' }}><i className="fas fa-calendar-alt"></i></span>
                            <div>
                              <p className="pp-modal-info-lbl">{t('pages.programs.deadlineLabel')}</p>
                              <p className={`pp-modal-info-val${tl.info?.expired ? ' pp-deadline-expired' : isDeadlineSoon(tl.info) ? ' pp-deadline-soon' : ' pp-deadline'}`}>
                                {tl.info?.expired
                                  ? `⏰ ${t('pages.programs.deadlinePassed')}`
                                  : isDeadlineSoon(tl.info)
                                    ? `🔴 ${tl.info?.formatted}`
                                    : `⏳ ${tl.info?.formatted}`}
                              </p>
                            </div>
                          </div>
                        )}
                        {selectedProgram.funding_type && (
                          <div className="pp-modal-info-item">
                            <span className="pp-modal-info-icon" style={{ background:'#16a34a20', color:'#16a34a' }}><i className="fas fa-money-bill-wave"></i></span>
                            <div><p className="pp-modal-info-lbl">{t('pages.programs.funding')}</p><p className="pp-modal-info-val">{selectedProgram.funding_type}</p></div>
                          </div>
                        )}
                        {selectedProgram.location && (
                          <div className="pp-modal-info-item">
                            <span className="pp-modal-info-icon" style={{ background:'#2563eb20', color:'#2563eb' }}><i className="fas fa-map-marker-alt"></i></span>
                            <div><p className="pp-modal-info-lbl">{t('pages.programs.location')}</p><p className="pp-modal-info-val">{selectedProgram.location}</p></div>
                          </div>
                        )}
                        {selectedProgram.duration && (
                          <div className="pp-modal-info-item">
                            <span className="pp-modal-info-icon" style={{ background:'#7c3aed20', color:'#7c3aed' }}><i className="fas fa-clock"></i></span>
                            <div><p className="pp-modal-info-lbl">{t('pages.programs.durationLabel')}</p><p className="pp-modal-info-val">{selectedProgram.duration}</p></div>
                          </div>
                        )}
                        {selectedProgram.end_date && (
                          <div className="pp-modal-info-item">
                            <span className="pp-modal-info-icon" style={{ background:'#0891b220', color:'#0891b2' }}><i className="fas fa-calendar-check"></i></span>
                            <div><p className="pp-modal-info-lbl">{t('pages.programs.datesLabel')}</p><p className="pp-modal-info-val">{selectedProgram.end_date}</p></div>
                          </div>
                        )}
                        {selectedProgram.target_group && (
                          <div className="pp-modal-info-item">
                            <span className="pp-modal-info-icon" style={{ background:'#ea580c20', color:'#ea580c' }}><i className="fas fa-users"></i></span>
                            <div><p className="pp-modal-info-lbl">{t('pages.programs.targetGroupLabel')}</p><p className="pp-modal-info-val">{selectedProgram.target_group}</p></div>
                          </div>
                        )}
                        {selectedProgram.format && (
                          <div className="pp-modal-info-item">
                            <span className="pp-modal-info-icon" style={{ background:'#db277720', color:'#db2777' }}><i className="fas fa-layer-group"></i></span>
                            <div><p className="pp-modal-info-lbl">{t('pages.programs.formatLabel')}</p><p className="pp-modal-info-val">{selectedProgram.format}</p></div>
                          </div>
                        )}
                        {selectedProgram.source && (
                          <div className="pp-modal-info-item">
                            <span className="pp-modal-info-icon" style={{ background:'#47556920', color:'#475569' }}><i className="fas fa-building"></i></span>
                            <div><p className="pp-modal-info-lbl">{t('pages.programs.organizerLabel')}</p><p className="pp-modal-info-val">{selectedProgram.source}</p></div>
                          </div>
                        )}
                      </div>
                    )}
                    {(() => {
                      const cleanDesc = stripDuplicateFieldLines(selectedProgram.description);
                      return cleanDesc && (
                        <div className="pp-modal-section">
                          <h4><i className="fas fa-align-left"></i> {t('pages.programs.description')}</h4>
                          <p style={{ whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{cleanDesc}</p>
                        </div>
                      );
                    })()}
                    {String(selectedProgram.media_url || '').trim() && (() => {
                      const mu = String(selectedProgram.media_url).trim();
                      const yt = getYoutubeEmbedUrl(mu);
                      const vm = getVimeoEmbedUrl(mu);
                      return (
                        <div className="pp-modal-section">
                          <h4><i className="fas fa-photo-video"></i> {t('pages.programs.mediaLabel')}</h4>
                          {yt ? (
                            <div style={{ position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:10, background:'#111' }}>
                              <iframe title="Program media" src={yt} style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', border:0 }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                            </div>
                          ) : vm ? (
                            <div style={{ position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:10, background:'#111' }}>
                              <iframe title="Program media" src={vm} style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', border:0 }} allowFullScreen />
                            </div>
                          ) : /\.(mp4|webm|ogg)(\?|$)/i.test(mu) ? (
                            <video src={mu} controls style={{ width:'100%', maxHeight:320, borderRadius:10, background:'#111' }} />
                          ) : (
                            <a href={mu} target="_blank" rel="noopener noreferrer" className="pp-apply-btn" style={{ display:'inline-flex', alignItems:'center', gap:8, textDecoration:'none' }}>
                              <i className="fas fa-external-link-alt"></i> {t('pages.programs.openMediaLabel')}
                            </a>
                          )}
                        </div>
                      );
                    })()}
                    {(() => {
                      const cleanFullText = stripDuplicateFieldLines(selectedProgram.full_text);
                      const cleanDesc = stripDuplicateFieldLines(selectedProgram.description);
                      return cleanFullText && cleanFullText !== cleanDesc && (
                        <div className="pp-modal-section">
                          <h4><i className="fas fa-info-circle"></i> {t('pages.programs.additionalDetailsLabel')}</h4>
                          <p style={{ whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{cleanFullText}</p>
                        </div>
                      );
                    })()}
                    {selectedProgram.requirements && (
                      <div className="pp-modal-section">
                        <h4><i className="fas fa-check-circle"></i> {t('pages.programs.requirementsLabel')}</h4>
                        <p style={{ whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{selectedProgram.requirements}</p>
                      </div>
                    )}
                    {selectedProgram.benefits && (
                      <div className="pp-modal-section">
                        <h4><i className="fas fa-gift"></i> {t('pages.programs.benefitsLabel')}</h4>
                        <p style={{ whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{selectedProgram.benefits}</p>
                      </div>
                    )}
                    {selectedProgram.categories?.length > 0 && (
                      <div className="pp-modal-section">
                        <h4><i className="fas fa-folder"></i> {t('pages.programs.categoriesLabel')}</h4>
                        <div className="pp-modal-cats">
                          {selectedProgram.categories.map((cat,i) => <span key={i} className="pp-modal-cat-badge">{cat}</span>)}
                        </div>
                      </div>
                    )}
                    {applicationFormInfo?.applicationForm?.enabled && applicationFormInfo?.alreadyApplied && applicationFormInfo?.myApplication && (() => {
                      const app = applicationFormInfo.myApplication;
                      const statusLabels = {
                        pending:  { cls: 'apm-status-pending',  icon: 'fa-hourglass-half', label: t('pages.programs.pending') },
                        accepted: { cls: 'apm-status-accepted', icon: 'fa-check-circle',   label: t('pages.programs.acceptedStatus') },
                        rejected: { cls: 'apm-status-rejected', icon: 'fa-times-circle',    label: t('pages.programs.notAcceptedStatus') },
                        attended: { cls: 'apm-status-attended', icon: 'fa-user-check',      label: t('pages.programs.attendedStatus') },
                      };
                      const meta = statusLabels[app.status] || statusLabels.pending;
                      return (
                        <div className="pp-modal-section">
                          <h4><i className="fas fa-file-signature"></i> {t('pages.programs.yourApplicationLabel')}</h4>
                          <div className="apm-status-panel">
                            <div className="apm-status-row">
                              <span className={`apm-status-badge ${meta.cls}`}><i className={`fas ${meta.icon}`}></i> {meta.label}</span>
                            </div>
                            {app.status === 'pending' && (
                              <p className="apm-status-msg">{t('pages.programs.appStatusPendingMsg')}</p>
                            )}
                            {app.status === 'accepted' && (
                              <p className="apm-status-msg">{t('pages.programs.appAcceptedMsg')}{app.organizerMessage ? ` ${app.organizerMessage}` : ''}</p>
                            )}
                            {app.status === 'rejected' && (
                              <p className="apm-status-msg">{t('pages.programs.appNotAcceptedMsg')}{app.organizerMessage ? ` ${app.organizerMessage}` : ''}</p>
                            )}
                            {app.status === 'attended' && (
                              <p className="apm-status-msg">{t('pages.programs.appAttendedMsg')}{app.organizerMessage ? ` ${app.organizerMessage}` : ''} {t('pages.programs.appCertificateMayFollow')}</p>
                            )}
                            {app.status === 'attended' && !app.orgReviewSubmitted && !reviewSubmitted && (
                              <div className="apm-review-box">
                                <p className="apm-review-title">{t('pages.programs.rateOrgTitle')}</p>
                                <div className="apm-stars">
                                  {[1,2,3,4,5].map(n => (
                                    <button
                                      type="button"
                                      key={n}
                                      className={`apm-star${n <= (reviewHoverRating || reviewRating) ? ' apm-star-filled' : ''}`}
                                      onMouseEnter={() => setReviewHoverRating(n)}
                                      onMouseLeave={() => setReviewHoverRating(0)}
                                      onClick={() => setReviewRating(n)}
                                      aria-label={t('pages.programs.starAriaLabel', { n })}
                                    >
                                      <i className="fas fa-star"></i>
                                    </button>
                                  ))}
                                </div>
                                <textarea
                                  className="apm-review-textarea"
                                  placeholder={t('pages.programs.optionalCommentPlaceholder')}
                                  rows={2}
                                  value={reviewComment}
                                  onChange={e => setReviewComment(e.target.value)}
                                />
                                <button
                                  type="button"
                                  className="apm-review-submit clay-press clay-focus-glow"
                                  disabled={!reviewRating || reviewSubmitting}
                                  onClick={submitOrgReview}
                                >
                                  {reviewSubmitting ? t('pages.programs.submittingLabel') : t('pages.programs.submitReviewLabel')}
                                </button>
                              </div>
                            )}
                            {app.status === 'attended' && (app.orgReviewSubmitted || reviewSubmitted) && (
                              <p className="apm-review-thanks"><i className="fas fa-heart"></i> {t('pages.programs.thanksForFeedback')}</p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    <div className="pp-modal-footer">
                      {((selectedProgram.url && selectedProgram.url !== '#')||selectedProgram.website) && (
                        <a href={(selectedProgram.url && selectedProgram.url !== '#') ? selectedProgram.url : selectedProgram.website} target="_blank" rel="noopener noreferrer" className="pp-apply-btn" style={{ background:getSectorColor(selectedProgram) }}
                          onClick={() => trackApplyClick(selectedProgram.id || selectedProgram._id)}>
                          <i className="fas fa-external-link-alt"></i> {t('pages.programs.applyNowLabel')}
                        </a>
                      )}
                      {applicationFormInfo?.applicationForm?.enabled && !applicationFormInfo?.alreadyApplied && (
                        <button
                          type="button"
                          className="pp-apply-btn clay-press clay-focus-glow"
                          style={{ background: getSectorColor(selectedProgram), border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                          onClick={handleApplyClick}
                        >
                          <i className="fas fa-file-signature"></i> {t('pages.programs.applyLabel')}
                        </button>
                      )}
                      <button className="pp-copy-link-btn" onClick={e => copyProgramLink(selectedProgram, e)} title={t('pages.programs.copyLinkTooltip')}>
                        <i className="fas fa-link"></i> {t('pages.programs.copyLinkLabel')}
                      </button>
                      {isAuthenticated && (
                        <button
                          type="button"
                          className={`pp-copy-link-btn pp-save-btn-modal${savedProgramIds.has(String(selectedProgram.id||selectedProgram._id)) ? ' pp-save-active' : ''}`}
                          onClick={e => toggleSaveProgram(e, selectedProgram)}
                          title={savedProgramIds.has(String(selectedProgram.id||selectedProgram._id)) ? t('pages.programs.unsaveProgram') : t('pages.programs.saveProgram')}
                        >
                          <i className={savedProgramIds.has(String(selectedProgram.id||selectedProgram._id)) ? 'fas fa-bookmark' : 'far fa-bookmark'}></i>
                          {' '}{savedProgramIds.has(String(selectedProgram.id||selectedProgram._id)) ? t('pages.programs.unsaveProgram') : t('pages.programs.saveProgram')}
                        </button>
                      )}
                      {selectedProgram.contact_email && (
                        <a href={`mailto:${selectedProgram.contact_email}`} className="pp-contact-link">
                          <i className="fas fa-envelope"></i> {selectedProgram.contact_email}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {showApplyFormModal && selectedProgram && applicationFormInfo?.applicationForm && (
          <ApplyProgramModal
            program={selectedProgram}
            questions={applicationFormInfo.applicationForm.questions || []}
            apiBase={API_BASE}
            onClose={() => setShowApplyFormModal(false)}
            onSubmitted={handleApplicationSubmitted}
          />
        )}

      </div>
    </>
  );
}

export default Programs;
